/**
 * Chat-enabled idea input for homepage streaming conversation.
 * Unified chat UI with Lev's greeting as first message.
 * Expands into modal mode when conversation starts to prevent background scroll.
 */
"use client";

import { useEffect, useRef, useState, useCallback, useMemo, memo, type ReactNode } from "react";
import { useCoalescedPersist } from "@/hooks/useCoalescedPersist";
import { createPortal } from "react-dom";
import dynamic from "next/dynamic";
import { useAuth } from "@clerk/nextjs";
import { experimental_useObject as useObject } from "@ai-sdk/react";
import { ArrowUp, FileUp, Loader2, Paperclip, SkipBack, SkipForward } from "lucide-react";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";
import { Button } from "@/components/atoms/primitives/Button";
import {
  OnboardingResponseSchema,
  mergePartialResults,
  mergeWithDefaults,
  buildSkipResult,
  hasCompleteBigIdeaData,
  hasMinimumBigIdeaData,
  type OnboardingResult,
  type PartialOnboardingResult,
} from "@/lib/onboarding/schema";
import { resolveCompletedOnboardingResult } from "@/lib/onboarding/buildReadiness";
import {
  saveOnboardingChat,
  saveOnboardingSessionBackup,
  peekOnboardingChat,
  peekOnboardingSessionBackup,
  consumeOnboardingChat,
  restoreOnboardingFromBackup,
  clearOnboardingBackups,
  type OnboardingChatPayload,
} from "@/lib/onboarding/session";
import { resolvePartnerSlug } from "@/lib/onboarding/handoff";
import { composeDeckUploadNotice } from "@/lib/onboarding/uploadNotice";
import {
  DeckParseProgressSchema,
  type DeckParseProgressCounts,
} from "@/lib/onboarding/deckParseProgress";
import { DeckParseProgressBar } from "@/components/molecules/landing/DeckParseProgressBar";
import { FactsInspectorGate } from "@/components/molecules/landing/FactsInspectorGate";
import {
  peekResumeChat,
  clearResumeChat,
  claimResumeRestoreTracking,
} from "@/lib/onboarding/resumeChat";
import { useLandingChatPresenceStore } from "@/state/landingChatPresenceStore";
import { NavigationBlocker } from "@/components/molecules/modals/NavigationBlocker";
import { extractFirstUrl } from "@/lib/site-snapshot";
import { detectLinkedInAnswer } from "@/lib/onboarding/linkedinDetect";
import { useAiDebug } from "@/components/providers/AiDebugProvider";
import { useOnboardingHandoff } from "@/components/molecules/landing/useOnboardingHandoff";
import { useOnboardingSessionLifecycle } from "@/components/molecules/landing/useOnboardingSessionLifecycle";
import { useOnboardingConversation } from "@/components/molecules/landing/useOnboardingConversation";
import {
  useMaterialsParseState,
  type MaterialsSlot,
} from "@/components/molecules/landing/useMaterialsParseState";
import type { DebugMessage } from "@/components/molecules/debug/AiPromptDebugDrawer";
import {
  trackOnboardingChatStarted,
  trackOnboardingChatMessageSent,
  trackOnboardingChatSkipClicked,
  trackOnboardingChatReset,
  trackOnboardingChatBuildConfirmed,
  trackOnboardingChatBuildDeclined,
  trackOnboardingChatRestored,
  trackOnboardingChatError,
  trackOnboardingChatRetryClicked,
  trackLandingPageCustomEvent,
  trackOnboardingMaterialsParseFailed,
  trackOnboardingBuildCtaGated,
  trackOnboardingAnswerBeforeReply,
  trackProgramCtaClicked,
  trackProgramMaterialsSubmitted,
  trackProgramFirstReadShown,
  trackProgramBuildClicked,
  trackDeckUploadFailed,
} from "@/lib/analytics/client";
import { PROGRAMS, type ProgramSlug } from "@/constants/programs";
import { parseDocxToText } from "@/lib/utils/docx";
import { parsePptxToText } from "@/lib/utils/pptx";
import { parsePdfToText } from "@/lib/utils/pdf";
import { captureClientError } from "@/lib/sentry/capture-client";
import { isChunkLoadError, isDeckNoTextError } from "@/lib/utils/shared";
import {
  classifyDeckReadError,
  classifyDeckUploadError,
  deckUploadFailureMessage,
  deckUploadFormatOf,
  deckUploadSizeBucket,
  isDeckUploadFailureKind,
  type DeckUploadFailureKind,
  type DeckUploadPhase,
} from "@/lib/utils/deckUploadErrors";
import {
  getDocumentKind,
  parseTextLikeFile,
  fileSizeBucket,
  DOCUMENT_UPLOAD_MAX_BYTES,
  DOCUMENT_ACCEPT_STRING,
  type SupportedFileKind,
} from "@/lib/utils/file_upload";
import type { LandingPageSlug } from "@/lib/analytics/events";
import {
  MATERIALS_NUDGE_MESSAGE,
  MATERIALS_NUDGE_STATUS_LINE,
} from "@/lib/onboarding/materialsNudge";
import { STREAM_ANCHOR_ATTR, useChatStreamScroll } from "@/hooks/chat/scroll";
import { UPLOAD_NAMESPACES, ONBOARDING_DECK_CLIENT_PATH_PREFIX } from "@/services/upload/uploadNamespaces";
import {
  ONBOARDING_LOADING_MESSAGES,
  LEV_GREETING_HEADLINE,
  LEV_GREETING_DETAILS,
  LEV_GREETING_DETAILS_STREAM_FRAMES,
  INPUT_PLACEHOLDER,
  MAX_USER_MESSAGES,
  WARNING_THRESHOLD,
  PROGRESS_INCREMENTS,
  STREAM_TIMEOUT_MS,
  HEARTBEAT_TIMEOUT_MS,
} from "@/constants/onboarding/config";

// Lazy so the panel's chunk downloads only once FactsInspectorGate admits a viewer.
const OnboardingFactsInspector = dynamic(
  () => import("@/components/molecules/landing/OnboardingFactsInspector"),
  { ssr: false },
);

export interface IdeaChatInputProps {
  /**
   * Called when user is ready to proceed (either via chat completion or skip).
   * May resolve false when the handoff failed (e.g. the Skip finalize push
   * didn't reach the server) — the component then shows a retry affordance
   * instead of staying in the building state.
   */
  onComplete: (result: OnboardingResult) => void | Promise<boolean>;
  className?: string;
  initialIdea?: string;
  isSubmitting?: boolean;
  landingPageSlug?: LandingPageSlug;
  /**
   * Sends `text` as the founder's first message (opening the expanded chat).
   * Each new `nonce` is a new request, so re-selecting the same starter works.
   * Once a conversation has started, a request only re-expands the chat; it
   * never injects a second opener mid-conversation. `source` labels the
   * `message_sent` event (default "capability_card").
   */
  sendRequest?: { text: string; nonce: number; source?: LandingStarterSource };
  /**
   * Suppresses Lev's display-only greeting (headline + streamed intro) so the
   * idle box is just the input. The greeting is never sent to the API or
   * persisted, so hiding it does not change the conversation.
   */
  hideGreeting?: boolean;
  /**
   * LEV-2359: set by a program lander only when `accelerators-straight-in` is
   * ON. Adds the deck-first drop zone and chips to the empty chat, sends
   * `program` with every chat turn and fires the program_* events. It never
   * touches the greeting: the lander hides that with hideGreeting. Absent →
   * the homepage chat, unchanged.
   */
  program?: ProgramSlug;
  /**
   * `accelerators-row` ON (read by the lander): the founder confirms the
   * program on their canvas, and the chat route shortens the program
   * interview to match. A dropped deck or a pasted website as the first
   * message gets a fixed reply asking for the founder's LinkedIn instead of a
   * model turn, as does a LinkedIn sent while that read still runs (a fixed
   * line saying the read is running). A reply announcing the read is followed
   * up when the parse lands, or answered with a fixed reply asking for the
   * key facts as text if it fails. Ignored without `program`.
   */
  programLandsOnCanvas?: boolean;
  /**
   * Reports whether the chat holds anything of the founder's yet: a message
   * (sent or restored), a file being read, or a saved chat offered for
   * restore. The program lander's testing skip is offered only while false,
   * because its hand-off merges into whatever this chat already holds.
   */
  onStartedChange?: (started: boolean) => void;
}

const MIN_IDEA_LENGTH = 10;

// The home page's composer pill. The idle program card boxes its composer in
// the same surface, so the two chat boxes MUST share this one class list.
const COMPOSER_SURFACE_CLASSES =
  "border border-landing-bg-medium rounded-[29px] shadow-[0_8px_32px_rgba(0,0,0,0.08)]";

// T.A. feedback (2026-10-30, Mockup 1 follow-up): the composer's own
// placeholder now names the "just start chatting" path alone — the big drop
// zone above it owns the upload ask (title below), so the two never repeat
// each other.
const PROGRAM_IDLE_PLACEHOLDER = "Tell Lev what you're building, or paste your website";
// Drop zone title/sub-line (idle only). The sub-line names the file formats
// once; the title and the card heading above it must not repeat each other
// (LEV-2710's invariant, carried into this pass).
const PROGRAM_DROP_ZONE_TITLE = "Drop your pitch deck or application";
const PROGRAM_DROP_ZONE_SUBTEXT = "or click to choose a file. PDF, PowerPoint or Word";
// Lev's fixed first reply to a deck dropped on a program lander whose program
// is confirmed on the canvas. It asks the founder for their LinkedIn, so it
// MUST stay in PROGRAM_FIXED_ASKS: it ends on the reason for the ask, not on
// the question mark, and decideMaterialsNudge would otherwise read it as "Lev
// announced the read" and follow it up in the founder's name.
// File-neutral (LEV-2517): the program drop zone invites the founder's
// current application as well as a deck, so a dropped PDF/PPTX is not always
// a deck. These replies say "file", never "deck".
const PROGRAM_DECK_FIRST_REPLY =
  "Got your file. I'm reading it now. While I do, what's your LinkedIn? It helps me write your team answers.";
// The same reply to a pasted website whose read was queued (same invariant).
const PROGRAM_SITE_FIRST_REPLY =
  "Got your website. I'm reading it now. While I do, what's your LinkedIn?";
// Lev's fixed reply when a read fails after Lev announced it. It MUST end in
// a question: the announcement left the founder nothing to answer.
const PROGRAM_DECK_UNREADABLE_REPLY =
  "I couldn't read your file. Could you paste the key slides here as text: the problem, your solution, traction and your ask?";
const PROGRAM_SITE_UNREADABLE_REPLY =
  "I couldn't read your website. Could you tell me in a few lines what you're building, who it's for and how far along you are?";
// Lev's fixed reply when a deck Lev already said it was reading never reached
// the server (session, blob PUT or parse POST failed). It MUST end in a
// question: it replaces the LinkedIn ask the founder has not answered yet.
const PROGRAM_DECK_NOT_UPLOADED_REPLY =
  "I couldn't open your file after all. Could you try uploading it again, or tell me in a few lines what you're building?";
// A first message that is a pasted website and little else gets the fixed
// reply; anything longer is the founder talking, and goes to Lev.
const PROGRAM_SITE_ONLY_MAX_EXTRA_CHARS = 40;
// Same bound for a LinkedIn answer acknowledged without a model turn.
const PROGRAM_LINKEDIN_ONLY_MAX_EXTRA_CHARS = 40;
// A program lander's drop zone invites the founder's current application as a
// DOCX/TXT/MD upload too, and a full application can run well past the chat
// route's message cap (MAX_MESSAGE_CONTENT_LENGTH = 20,000,
// src/app/api/onboarding/chat/route.ts) — sent as-is, that 400s the whole
// chat instead of starting it. Leaves headroom below the cap for the trimmed
// marker itself. Homepage uploads never pass through this: an application is
// a program-lander concept.
const PROGRAM_APPLICATION_TEXT_MAX_CHARS = 19_000;
// The founder must be able to tell their own message was cut, so the marker
// rides inside the message text rather than a toast that scrolls away.
const PROGRAM_APPLICATION_TRIMMED_MARKER = "\n\n(trimmed to fit)";

function trimProgramApplicationText(text: string): string {
  if (text.length <= PROGRAM_APPLICATION_TEXT_MAX_CHARS) return text;
  return text.slice(0, PROGRAM_APPLICATION_TEXT_MAX_CHARS) + PROGRAM_APPLICATION_TRIMMED_MARKER;
}


/**
 * Lev's fixed acknowledgement of a LinkedIn sent while the read still runs.
 * It MUST NOT end in a question (decideMaterialsNudge reads that as "Lev
 * announced the read" and asks for it when the parse lands) and MUST NOT
 * repeat the link.
 */
function programReadingAck(sources: ReadonlySet<MaterialsSlot>): string {
  const noun =
    sources.has("deck") && sources.has("site")
      ? "file and website"
      : sources.has("site")
        ? "website"
        : "file";
  return `Thanks. I'm still reading your ${noun}. I'll share my read as soon as it's done.`;
}

// Synthetic greeting message (display-only, never sent to the API). Always
// the default copy: a program lander hides it via hideGreeting instead of
// swapping it, so there is only ever one greeting to keep in sync.
const LEV_GREETING_MESSAGE = { role: "assistant" as const, content: LEV_GREETING_HEADLINE };

// Async deck-parse polling (LEV-1243): the vision parse runs as an Inngest
// job; the client polls the status GET until the critique lands. The timeout
// is an IDLE window: it restarts whenever the reported deck progress changes
// or a tick is skipped because the tab is hidden, so a big deck that keeps
// moving (or a founder who tabs away) is not abandoned at 5 minutes. The hard cap
// MUST exceed the parse step's own limit (maxDuration 1800 s in
// src/app/api/inngest/route.ts) plus queueing, or a live parse is given up on;
// raise it with that limit.
const MATERIALS_PARSE_POLL_INTERVAL_MS = 3_000;
const MATERIALS_PARSE_POLL_TIMEOUT_MS = 5 * 60 * 1000;
const MATERIALS_PARSE_POLL_MAX_MS = 35 * 60 * 1000;
const DeckProgressCountsSchema = DeckParseProgressSchema.pick({
  phase: true,
  batchesDone: true,
  batchesTotal: true,
});

/**
 * Outcome of a server-side materials parse (deck or website). Discriminated so
 * the caller can distinguish a ready critique from a genuine failure and
 * surface the latter (with the HTTP status) instead of silently degrading.
 * `phase` says where it failed: `post` (staging POST rejected), `poll` (never
 * reached a terminal status before the deadline), or `job` (the Inngest job
 * itself reported failure / lost the marker).
 */
type MaterialsParseResult =
  | { ok: true; readDue?: MaterialsSlot[] }
  | {
      ok: false;
      phase: "blob-upload" | "post" | "poll" | "job";
      status: number | null;
      reason: string;
      /** The founder-facing cause; absent when nothing better than "unknown" is known. */
      failureKind?: DeckUploadFailureKind;
    };

/** Every file kind the onboarding picker takes, for the unsupported_format message. */
const ONBOARDING_ACCEPTED_FILES = "PDF, DOCX, PPTX, TXT, or MD file";

/**
 * Wait `ms`, but resolve early the moment `signal` aborts (instead of always
 * burning the full inter-poll delay after the component has unmounted). Never
 * rejects — the caller re-checks `signal.aborted` after awaiting.
 */
function delayUnlessAborted(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

type MaterialsSlotStatus = "none" | "processing" | "ready" | "failed";

/** Parses the chat route's `x-lev-read-due` response header (comma-joined MaterialsSource values, "" when none). */
function parseReadDueHeader(value: string | null): Set<MaterialsSlot> {
  const sources = new Set<MaterialsSlot>();
  if (!value) return sources;
  for (const raw of value.split(",")) {
    const s = raw.trim();
    if (s === "deck" || s === "site") sources.add(s);
  }
  return sources;
}

/** One request body per source: a staged deck blob reference or a website URL. */
type MaterialsParseBody =
  | { deckBlob: { pathname: string; fileName: string; size: number }; clientTextPreview?: string }
  | { url: string };

/** linkedin.com (any subdomain) is the founder's profile, never their website. */
function isLinkedInHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === "linkedin.com" || host.endsWith(".linkedin.com");
}

/** The message with every token naming `host` removed. */
function textBesidesUrl(text: string, host: string): string {
  const bareHost = host.replace(/^www\./, "");
  return text
    .split(/\s+/)
    .filter((token) => !token.toLowerCase().includes(bareHost))
    .join(" ")
    .trim();
}

/**
 * The founder's last message is their LinkedIn and nothing that needs an
 * answer: no question, and little text besides the link.
 */
function isBareLinkedInAnswer(messages: Array<{ role: "user" | "assistant"; content: string }>): boolean {
  if (!detectLinkedInAnswer(messages)) return false;
  // The link's own query string (?utm_source=share from the mobile app) is not a question.
  const besides = textBesidesUrl(messages[messages.length - 1].content, "linkedin.com");
  if (besides.includes("?")) return false;
  return besides.length <= PROGRAM_LINKEDIN_ONLY_MAX_EXTRA_CHARS;
}

/**
 * POST a deck blob reference or a website URL to the materials parse route,
 * kicking off the server-side Inngest parse job. Returns immediately after
 * the POST — does NOT poll. The caller sends the chat message right away and
 * polls in the background via `pollMaterialsParseStatus`.
 */
/** Deadline for the materials POST: it gates the chat turn, so a stall must release the composer (PR #703 review). Exported for the component test. */
export const MATERIALS_PARSE_POST_TIMEOUT_MS = 10_000;

async function postMaterialsParse(
  body: MaterialsParseBody,
  signal: AbortSignal,
): Promise<MaterialsParseResult> {
  // Caller abort and the deadline are SEPARATE signals: a timeout reports as a failure
  // (host released, chat proceeds), not as a user abort that would pin the host as parsed.
  const deadline = new AbortController();
  const abortForCaller = () => deadline.abort();
  if (signal.aborted) abortForCaller();
  signal.addEventListener("abort", abortForCaller, { once: true });
  const timer = setTimeout(() => deadline.abort(), MATERIALS_PARSE_POST_TIMEOUT_MS);
  let res: Response;
  let bodyKind: unknown;
  try {
    res = await fetch("/api/onboarding/materials/parse", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: deadline.signal,
    });
    // Read the error body while the deadline and caller abort still apply.
    if (!res.ok) {
      try {
        bodyKind = ((await res.json()) as { kind?: unknown } | null)?.kind;
      } catch {
        /* expected: a non-JSON or stalled error body (proxy page, deadline); the status still classifies it */
      }
    }
  } catch {
    /* expected: user abort, deadline, or transient network error with fallback error object */
    if (signal.aborted) {
      return { ok: false, phase: "post", status: null, reason: "aborted" };
    }
    // The 10 s POST deadline is a stalled request, not a slow read: the file
    // is already in Blob storage, so "didn't finish uploading" is the honest copy.
    if (deadline.signal.aborted) {
      return { ok: false, phase: "post", status: null, reason: "POST timeout", failureKind: "upload_failed" };
    }
    return { ok: false, phase: "post", status: null, reason: "POST network error", failureKind: "upload_failed" };
  } finally {
    clearTimeout(timer);
    signal.removeEventListener("abort", abortForCaller);
  }
  if (!res.ok) {
    return {
      ok: false,
      phase: "post",
      status: res.status,
      reason: `POST ${res.status}`,
      failureKind: isDeckUploadFailureKind(bodyKind)
        ? bodyKind
        : classifyDeckUploadError({ data: { httpStatus: res.status } }),
    };
  }
  return { ok: true };
}

/** One status GET: the slot's status, a terminal HTTP rejection, or nothing usable this tick. */
type MaterialsStatusRead =
  | {
      kind: "status";
      status: MaterialsSlotStatus;
      deckProgress: unknown;
      readDue?: MaterialsSlot[];
      failureKind?: DeckUploadFailureKind;
    }
  | { kind: "rejected"; httpStatus: number }
  | { kind: "retry" };

async function readMaterialsSlotStatus(
  slot: MaterialsSlot,
  signal: AbortSignal,
): Promise<MaterialsStatusRead> {
  try {
    const statusRes = await fetch("/api/onboarding/materials/parse", {
      method: "GET",
      credentials: "same-origin",
      signal,
    });
    if (!statusRes.ok) {
      if (statusRes.status === 401 || statusRes.status === 403 || statusRes.status === 404) {
        return { kind: "rejected", httpStatus: statusRes.status };
      }
      return { kind: "retry" };
    }
    const body = (await statusRes.json()) as {
      deck?: MaterialsSlotStatus;
      site?: MaterialsSlotStatus;
      status: MaterialsSlotStatus;
      deckProgress?: unknown;
      readDue?: MaterialsSlot[];
      deckFailureKind?: unknown;
    };
    return {
      kind: "status",
      status: body[slot] ?? body.status,
      deckProgress: body.deckProgress,
      readDue: body.readDue,
      // Validated, never trusted: a value outside the kind list (an older or
      // newer server) falls through to "unknown" instead of reaching the copy lookup.
      failureKind:
        slot === "deck" && isDeckUploadFailureKind(body.deckFailureKind) ? body.deckFailureKind : undefined,
    };
  } catch {
    /* benign: transient poll failure (or a fetch aborted by unmount); the caller re-checks signal.aborted */
    return { kind: "retry" };
  }
}

/** Maps a slot's terminal status to the poll outcome; null while still processing. */
function terminalOutcome(
  status: MaterialsSlotStatus,
  readDue?: MaterialsSlot[],
  failureKind?: DeckUploadFailureKind,
): MaterialsParseResult | null {
  if (status === "ready") return { ok: true, readDue };
  if (status === "failed") {
    return { ok: false, phase: "job", status: null, reason: "job reported failed", failureKind };
  }
  if (status === "none") {
    return { ok: false, phase: "job", status: null, reason: "parse marker missing" };
  }
  return null;
}

/**
 * Poll the materials parse status until the given slot's critique is ready.
 * Runs in the background after the chat is already unlocked — failures
 * surface as toasts, not blocking errors. Reads the slot's own status
 * (`deck` / `site`, LEV-1424) so a deck and a website in flight at the same
 * time each settle on their own; falls back to the aggregate `status` when
 * the response predates per-slot reporting.
 */
async function pollMaterialsParseStatus(
  slot: MaterialsSlot,
  signal: AbortSignal,
  onDeckProgress?: (progress: DeckParseProgressCounts) => void,
): Promise<MaterialsParseResult> {
  const aborted: MaterialsParseResult = { ok: false, phase: "poll", status: null, reason: "aborted" };
  const hardDeadline = Date.now() + MATERIALS_PARSE_POLL_MAX_MS;
  let idleDeadline = Date.now() + MATERIALS_PARSE_POLL_TIMEOUT_MS;
  let lastProgressKey: string | null = null;
  while (Date.now() < Math.min(idleDeadline, hardDeadline)) {
    if (signal.aborted) return aborted;
    await delayUnlessAborted(MATERIALS_PARSE_POLL_INTERVAL_MS, signal);
    if (signal.aborted) return aborted;
    // Hidden time is not idle time: no GET was made, so nothing was learned
    // about the parse. Without the restart a founder who tabs away for five
    // minutes gets a failure toast for a deck the server finished long ago.
    // The hard deadline still bounds a tab that never comes back.
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      idleDeadline = Date.now() + MATERIALS_PARSE_POLL_TIMEOUT_MS;
      continue;
    }
    const read = await readMaterialsSlotStatus(slot, signal);
    if (read.kind === "rejected") {
      return { ok: false, phase: "poll", status: read.httpStatus, reason: `poll ${read.httpStatus}` };
    }
    if (read.kind === "retry") continue;
    if (onDeckProgress && read.status === "processing") {
      const progress = DeckProgressCountsSchema.safeParse(read.deckProgress);
      if (progress.success) {
        const { phase, batchesDone, batchesTotal } = progress.data;
        const key = `${phase}:${batchesDone}:${batchesTotal}`;
        if (key !== lastProgressKey) {
          lastProgressKey = key;
          idleDeadline = Date.now() + MATERIALS_PARSE_POLL_TIMEOUT_MS;
        }
        onDeckProgress(progress.data);
      }
    }
    const outcome = terminalOutcome(read.status, read.readDue, read.failureKind);
    if (outcome) return signal.aborted ? aborted : outcome;
  }
  // One last read before declaring a timeout: the loop's final tick may have
  // been skipped (hidden tab) or lost to a transient error, and a parse that
  // is already done must never be reported as failed.
  if (signal.aborted) return aborted;
  const last = await readMaterialsSlotStatus(slot, signal);
  if (signal.aborted) return aborted;
  if (last.kind === "status") {
    const outcome = terminalOutcome(last.status, last.readDue, last.failureKind);
    if (outcome) return outcome;
  }
  return { ok: false, phase: "poll", status: null, reason: "poll timeout", failureKind: "timed_out" };
}
const MIN_TEXTAREA_HEIGHT = 24;
const MAX_TEXTAREA_HEIGHT = 240;
const EXPANDED_CHAT_HEIGHT = "50vh";
// Inline mode shows the two intro bubbles before the first send (and a
// minimized conversation after). The cap must clear both intro bubbles on the
// narrowest hero column (partner landers, 640px) so the intro never scrolls;
// the vh guard is for short viewports only. fixed on 2026-09-22: LEV-1777
const INLINE_CHAT_MAX_HEIGHT = "min(70vh, 560px)";
const INTRO_STREAM_DELAY_MS = 2500;
const INTRO_STREAM_INTERVAL_MS = 360;


export type StreamPhase = 'idle' | 'streaming-message' | 'streaming-result';

/** Homepage surfaces that send a canned starter as the first chat message. */
export type LandingStarterSource = "capability_card" | "training_topic" | "how_it_works_cta";

type LandingMessageSource = "text_input" | "file_upload" | LandingStarterSource;

type SubmitOptions = {
  source?: LandingMessageSource;
  /** Hold for the turn-end dispatcher instead of dropping while a turn is busy. */
  queueIfBusy?: boolean;
  /**
   * Lev's reply to a FIRST message, committed locally instead of a model turn.
   * Ignored on any later message, and lost if the send has to queue: the
   * message then goes to the model as usual.
   */
  localReply?: string;
};

function toDebugMessages(messages: Array<{ role: "user" | "assistant"; content: string }>): DebugMessage[] {
  return messages.map((m, i) => ({
    id: `msg-${i}`,
    role: m.role,
    content: m.content,
  }));
}

/**
 * Lightweight markdown renderer for chat messages.
 * Supports bold, lists, and basic formatting without heavy styling.
 */
const ChatMarkdown = memo(({ content }: { content: string }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }: { children?: ReactNode }) => (
        <p className="mb-2 last:mb-0">{children}</p>
      ),
      strong: ({ children }: { children?: ReactNode }) => (
        <strong className="font-semibold">{children}</strong>
      ),
      em: ({ children }: { children?: ReactNode }) => (
        <em className="italic">{children}</em>
      ),
      ol: ({ children }: { children?: ReactNode }) => (
        <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>
      ),
      ul: ({ children }: { children?: ReactNode }) => (
        <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>
      ),
      li: ({ children }: { children?: ReactNode }) => (
        <li>{children}</li>
      ),
    }}
  >
    {content}
  </ReactMarkdown>
));
ChatMarkdown.displayName = "ChatMarkdown";

/**
 * Check if a message ends with a question (ignoring trailing whitespace/quotes/closing punctuation).
 * Used to prevent premature completion when the AI asks a question but also sets status=complete.
 * @public Exported for testing
 */
export function messageEndsWithQuestion(message: string): boolean {
  // Trim and check if it ends with a question mark, allowing trailing quotes/closing punctuation.
  const trimmed = message.trim();
  // Match a question mark followed only by optional whitespace, quotes, or closing punctuation like ), !, .
  return /\?[\s"'”’)\]\}.!…]*$/.test(trimmed);
}

// Lev's fixed replies that ask the founder something but end on the reason
// for the ask, so messageEndsWithQuestion cannot see the question.
const PROGRAM_FIXED_ASKS: ReadonlySet<string> = new Set([PROGRAM_DECK_FIRST_REPLY]);

/**
 * True when Lev's message hands the floor to the founder: it ends in a
 * question, or it is one of Lev's fixed asks. Gates the materials follow-ups
 * only; the completion gate reads model output, never a fixed reply.
 */
function asksFounder(message: string): boolean {
  return messageEndsWithQuestion(message) || PROGRAM_FIXED_ASKS.has(message);
}

// "c", "C", "c)", "(c)", "c.", "option c" — a pick with nothing else typed.
const BARE_PICK_RE = /^(?:option\s+)?\(?([a-z])\)?\.?$/i;
// "d) hospitals", "d: hospitals", "d - hospitals", "d\nhospitals" — a pick
// followed by the founder's own text.
const PICK_WITH_TEXT_RE = /^(?:option\s+)?\(?([a-z])(?:\)|\.|:|\s*[-–—]+|\s*\n)\s*([\s\S]+)$/i;

/**
 * Resolve an answer to the lettered choices in the assistant message it
 * replies to, returning the text the founder picked.
 * e.g. "c" → "Venues / parks — fragmented operators but lots of operational pain"
 *
 * Callers send the result as the founder's message, so the persisted turn,
 * LevAI history, the claim flow and the fact extractor all see the picked
 * option — never a bare letter (a one-character "c" carries no fact).
 *
 * `prevAssistantContent` MUST be the message immediately before this answer:
 * a letter means nothing against any older question. An "Other — type your own answer" pick
 * resolves to the typed text; a bare Other pick, an unmatched letter, or any
 * other answer is returned unchanged.
 * @public Exported for testing
 */
export function expandLetterChoice(
  userContent: string,
  prevAssistantContent: string | undefined,
): string {
  if (!prevAssistantContent) return userContent;
  const trimmed = userContent.trim();
  const bare = BARE_PICK_RE.exec(trimmed);
  const withText = bare ? null : PICK_WITH_TEXT_RE.exec(trimmed);
  const letter = (bare ?? withText)?.[1]?.toLowerCase();
  if (!letter) return userContent;

  // Choice lines look like "a) **Bold text** — rest of line", optionally
  // indented by Markdown list formatting.
  const match = prevAssistantContent.match(new RegExp(`^\\s*\\(?${letter}\\)\\s+(.+)$`, "m"));
  if (!match) return userContent;
  const choiceText = match[1].replace(/\*\*/g, "").trim();
  const isOther = /^other\b/i.test(choiceText);

  if (bare) return isOther ? userContent : choiceText;
  return isOther ? withText![2].trim() : userContent;
}

/**
 * Append the founder's answer to the history as it will be sent: a lettered
 * pick is resolved against the message it directly follows (see
 * expandLetterChoice), so only an assistant message in the last slot counts.
 */
function appendUserAnswer(
  history: Array<{ role: "user" | "assistant"; content: string }>,
  text: string,
): Array<{ role: "user" | "assistant"; content: string }> {
  const last = history[history.length - 1];
  const content = expandLetterChoice(text, last?.role === "assistant" ? last.content : undefined);
  return [...history, { role: "user", content }];
}

/**
 * True when Lev has the floor handed to the founder: Lev spoke last, the
 * completion gate did not accept Lev's latest turn as complete, and the Build
 * CTA is not up. `completionAccepted` MUST be the gate's own verdict, never
 * the raw streamed status: the gate rejects a "complete" that ends in a
 * question, and that turn still owes Lev an answer. Adds no text
 * introspection of its own.
 * @public Exported for testing
 */
export function isAwaitingFounderReply(
  messages: ReadonlyArray<{ role: "user" | "assistant"; content: string }>,
  completionAccepted: boolean,
  showBuildConfirm: boolean,
): boolean {
  if (showBuildConfirm) return false;
  if (messages[messages.length - 1]?.role !== "assistant") return false;
  return !completionAccepted;
}

export default function IdeaChatInput({
  onComplete,
  className,
  initialIdea = "",
  isSubmitting = false,
  landingPageSlug,
  sendRequest,
  hideGreeting = false,
  program,
  programLandsOnCanvas = false,
  onStartedChange,
}: IdeaChatInputProps) {
  const [inputValue, setInputValue] = useState(initialIdea);
  const {
    messages,
    messagesRef,
    commitMessages,
    result,
    setResult,
    resultRef,
    stableResult,
    setStableResult,
    streamingMessage,
    setStreamingMessage,
    streamingMessageRef,
    accumulatedResultRef,
    rubricTypeDirtyRef,
    flushToAccumulator,
    resetConversationState,
    restoreConversationState,
  } = useOnboardingConversation();
  const [isReady, setIsReady] = useState(false);
  const [savedChat, setSavedChat] = useState<OnboardingChatPayload | null>(null);
  const savedChatRef = useRef(savedChat);
  savedChatRef.current = savedChat;
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const showRestorePromptRef = useRef(showRestorePrompt);
  showRestorePromptRef.current = showRestorePrompt;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isBuildingProject, setIsBuildingProject] = useState(false);
  const buildingMessage = ONBOARDING_LOADING_MESSAGES[0];
  // Handoff retry wrapper: when onComplete resolves false (finalize push
  // failed), swap the building spinner for a retry affordance.
  const { handoffState, begin: beginHandoff, retry: retryHandoff } = useOnboardingHandoff(onComplete);
  // Confirmation flow: show prompt when AI says complete, allow user to continue if they decline
  const [showBuildConfirm, setShowBuildConfirm] = useState(false);
  const [userDeclinedBuild, setUserDeclinedBuild] = useState(false);
  // Reset confirmation modal
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // True while "Start over" waits for the server to drop the session cookie;
  // the ref is the synchronous single-flight guard against a double confirm.
  const [isResetting, setIsResetting] = useState(false);
  const isResettingRef = useRef(false);
  // Bumped by every reset. Upload paths capture it before their first await
  // and drop their result when it moved: after a reset the cookie names the
  // NEXT conversation's session, so a stale parse POST or chat send would
  // land the discarded file in it.
  const conversationGenerationRef = useRef(0);
  // When user clicks Build while stream is still in progress, wait for it to finish
  const [waitingForStream, setWaitingForStream] = useState(false);
  const {
    inFlightParses,
    setInFlightParses,
    inFlightParsesCountRef,
    suppressedCompleteRef,
    isMaterialsSuppressed,
    setIsMaterialsSuppressed,
    anyParseReadyRef,
    anyReadStillDueRef,
    pendingNudgeSourcesRef,
    nudgeTargetSourcesRef,
    lastTurnReadDueRef,
    materialsEpochRef,
    gateWasSuppressedRef,
    deckProgress,
    setDeckProgress,
    visionParseAbortRef,
    siteParseAbortRef,
    parsedSiteHostsRef,
    deckStagingRef,
    nudgeHeldRef,
    materialsFastPathRef,
    readLostRef,
    resetMaterialsParse,
  } = useMaterialsParseState();
  // Delayed onboarding intro state — shows a waiting marker first, then streams on fresh load.
  const [introState, setIntroState] = useState<"idle" | "waiting" | "streaming" | "done">("idle");
  const [introStreamedContent, setIntroStreamedContent] = useState("");
  // Drives the deck-first drop zone and chips below. PROGRAMS entries are
  // module constants, so this stays referentially stable across renders. The
  // greeting itself is never program-specific (LEV-2359 dedupe): the lander
  // already asked for the deck in its own intro and passes hideGreeting, so
  // a program swap here would either duplicate that ask or never render.
  const programConfig = program ? PROGRAMS[program] : null;
  // The chat route shortens this founder's interview to the LinkedIn ask
  // (programContext.ts confirmOnCanvas); the client mirrors it here.
  const landsOnCanvas = programConfig !== null && programLandsOnCanvas;
  // program_first_read_shown fires once per conversation; Reset re-arms it.
  const programFirstReadTrackedRef = useRef(false);

  // Debug context (dev only - updates page-level debug drawer)
  const aiDebug = useAiDebug();
  // Bumped when a turn finishes or a materials parse settles so the facts
  // inspector refetches; harmless when FactsInspectorGate renders nothing.
  const [factsRefreshSignal, setFactsRefreshSignal] = useState(0);

  // Gates the sign-in-resume marker clear below — see signInResumeRestored.
  const { isLoaded: isClerkLoaded, isSignedIn } = useAuth();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasStartedRef = useRef(false);
  const { mintSessionOnce, ensureSession, detachSession } = useOnboardingSessionLifecycle();
  const introDelayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const introIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Guard against multiple auto-proceed calls (can happen with effect re-runs)
  const hasAutoProceedTriggeredRef = useRef(false);
  // Guard: prevents both timeout handler and onFinish from adding the same assistant message
  const turnMessageCommittedRef = useRef(false);
  // Refs to access current values inside timeout handler (avoids stale closures)
  type UseObjectReturn = ReturnType<typeof useObject<typeof OnboardingResponseSchema>>;
  const objectRef = useRef<UseObjectReturn['object']>(undefined);

  const submitRef = useRef<UseObjectReturn['submit'] | null>(null);
  // The submit that started the current generation, and every submit that
  // ever started one (see callbackOwner). Only a started-then-superseded
  // submit's callbacks are stale.
  const activeSubmitRef = useRef<UseObjectReturn['submit'] | null>(null);
  const startedSubmitsRef = useRef(new WeakSet<NonNullable<UseObjectReturn['submit']>>());
  const startGeneration = useCallback((fn: NonNullable<UseObjectReturn['submit']>) => {
    activeSubmitRef.current = fn;
    startedSubmitsRef.current.add(fn);
  }, []);
  // Latest handleSubmit for async callers (deck upload, parse nudge, queue
  // flush). They MUST go through this ref: a handleSubmit captured before an
  // await carries a stale streamPhase and drops or misfires the send.
  const handleSubmitRef = useRef<(overrideText?: string, opts?: SubmitOptions) => void>(() => {});

  // The founder's answer typed during the result tail. One slot; while it is
  // held the composer is locked. It is never in `messages` until it is sent,
  // so it can't land above Lev's reply. Ref for synchronous reads, state for
  // rendering.
  const queuedAnswerRef = useRef<string | null>(null);
  const [queuedAnswer, setQueuedAnswerState] = useState<string | null>(null);
  const setQueuedAnswer = useCallback((next: string | null) => {
    queuedAnswerRef.current = next;
    setQueuedAnswerState(next);
  }, []);
  // After a failed or empty turn the founder decides with the error toast in
  // view: the queued answer returns to the composer instead of auto-sending.
  const handBackQueuedAnswer = useCallback(() => {
    const queued = queuedAnswerRef.current;
    if (queued === null) return;
    setQueuedAnswer(null);
    setInputValue(queued);
  }, [setQueuedAnswer]);
  // A deck notice whose upload finished mid-turn. Sent by the turn-end
  // dispatcher only after any queued founder answer, one send per turn end.
  const queuedNoticeRef = useRef<string | null>(null);

  // Bumped once per finished turn (onFinish, error, stream timeout) — never
  // from isLoading, which the SDK clears BEFORE onFinish commits Lev's reply.
  // The turn-end dispatcher handles each value once, so each turn end sends
  // at most one message.
  const finishedTurnSeqRef = useRef(0);
  const handledTurnSeqRef = useRef(0);
  const [finishedTurnSeq, setFinishedTurnSeq] = useState(0);
  // A failed turn (error, empty reply) sends nothing at its end: the founder
  // retries with the error toast in view, and a held notice or nudge waits
  // for the end of that retry instead of jumping ahead of it.
  const lastTurnFailedRef = useRef(false);
  // A turn that ended with no reply. Rendered inline with a retry, not a
  // toast: a toast is gone in five seconds and the chat then looks like it is
  // still waiting (LEV-2450). Cleared by the next turn that starts.
  const [turnFailed, setTurnFailed] = useState(false);
  const markTurnFinished = useCallback((failed = false) => {
    lastTurnFailedRef.current = failed;
    finishedTurnSeqRef.current += 1;
    setFinishedTurnSeq(finishedTurnSeqRef.current);
  }, []);

  // Timeout recovery state - tracks when we've force-reset due to stuck loading
  const [hasTimedOut, setHasTimedOut] = useState(false);
  // Refs for timeout recovery
  const streamStartTimeRef = useRef<number | null>(null);
  const lastChunkTimeRef = useRef<number | null>(null);
  const timeoutCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Pending skip state: when user clicks Skip during streaming, defer until stream completes
  const [pendingSkip, setPendingSkip] = useState(false);
  // Ref mirror of pendingSkip for reading inside timeout handler (avoids stale closure)
  const pendingSkipRef = useRef(false);
  pendingSkipRef.current = pendingSkip;
  // Ref mirror of inputValue for reading inside executeSkipAction (avoids effect churn)
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;

  // PDF upload state
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Tracks mount so the upload `finally` doesn't setState after unmount.
  const isMountedRef = useRef(true);
  // First-turn session-mint gating (#541): while the first message waits for the
  // mint, block further sends (state disables the composer). Reset/unmount
  // cancel the deferred submit via conversationGenerationRef / isMountedRef.
  const [isAwaitingFirstMint, setIsAwaitingFirstMint] = useState(false);
  // A program deck whose fixed first reply already showed is still uploading:
  // the composer stays open for the LinkedIn answer while isParsingFile keeps
  // a second upload and Reset out.
  const [isUploadingAnnouncedDeck, setIsUploadingAnnouncedDeck] = useState(false);
  useEffect(() => {
    // MUST restore true on setup, not just false on cleanup: under React 19
    // Strict Mode the dev mount→unmount→remount replay would otherwise leave
    // the ref permanently false after the first cleanup, wedging the synchronous
    // mount guards (upload finally → isParsingFile stuck) (#534 review).
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const trackLandingMessageEvent = useCallback(
    (
      messageNumber: number,
      messageLength: number,
      source: LandingMessageSource,
    ) => {
      if (!landingPageSlug) return;
      trackLandingPageCustomEvent(landingPageSlug, "message_sent", {
        messageNumber,
        messageLength,
        source,
      });
    },
    [landingPageSlug],
  );

  const trackLandingFileEvent = useCallback(
    (fileKind: SupportedFileKind, source: "drag_drop" | "picker") => {
      if (!landingPageSlug) return;
      trackLandingPageCustomEvent(landingPageSlug, "file_added", {
        fileKind,
        source,
        hasStartedConversation: hasStartedRef.current,
      });
    },
    [landingPageSlug],
  );

  // Terminal analytics for a materials parse (deck or website — LEV-1431).
  // Decks always use "server_vision" (the async materials pipeline). A website
  // always goes through the server job, so it carries no strategy. Complements
  // file_added (fires before strategy selection).
  const trackMaterialsParsed = useCallback(
    (
      event:
        | {
            sourceType: "deck";
            strategy: "server_vision";
            outcome: "success" | "failure";
          }
        | { sourceType: "site"; outcome: "success" | "failure" },
    ) => {
      if (!landingPageSlug) return;
      trackLandingPageCustomEvent(landingPageSlug, "file_parsed", event);
    },
    [landingPageSlug],
  );

  const trackLandingConversionEvent = useCallback(
    (source: "build_confirmed" | "skip", finalResult: OnboardingResult) => {
      if (program && source === "build_confirmed") trackProgramBuildClicked({ program });
      if (!landingPageSlug) return;
      trackLandingPageCustomEvent(landingPageSlug, "conversion_submitted", {
        source,
        messageCount: messagesRef.current.filter((m) => m.role === "user").length,
        hasIdentity: !!(finalResult.identity?.name || finalResult.identity?.linkedinUrl),
        hasJourney: finalResult.journey?.stage !== "unknown",
      });
    },
    [landingPageSlug, program, messagesRef],
  );

  // Shared skip execution logic used by handleSkip, onFinish, and handleStreamTimeout.
  // Prefers the cumulative accumulated result (merged across all streaming turns)
  // over any per-turn `objectRef.current?.result` so Skip mid-turn-N still
  // carries forward data captured in turns 1..N-1.
  const executeSkipAction = useCallback(() => {
    const liveObj = objectRef.current?.result as PartialOnboardingResult | undefined;
    const cumulative = accumulatedResultRef.current;
    // Merge prior cumulative with any in-flight partial from the current turn.
    // next (liveObj) wins where defined; prior (cumulative) fills the gaps.
    const existing: PartialOnboardingResult | OnboardingResult | null =
      cumulative || liveObj
        ? mergePartialResults(cumulative, liveObj ?? null)
        : resultRef.current;
    const currentMessages = messagesRef.current;
    const userMessages = currentMessages.filter((m) => m.role === "user");
    const rawIdeaFallback = userMessages.length > 0
      ? userMessages.map((m) => m.content).join("\n\n")
      : inputValueRef.current.trim() || "Untitled idea";
    const skipResult = buildSkipResult(
      existing as PartialOnboardingResult | OnboardingResult | null,
      rawIdeaFallback,
    );

    // eslint-disable-next-line no-console -- intentional onboarding diagnostic log
    console.log("[IdeaChatInput] Skip executing with captured onboarding data", {
      messageCount: currentMessages.filter((m) => m.role === "user").length,
      status: skipResult.status,
      journeyStage: skipResult.journey?.stage ?? null,
      oneLiner: skipResult.bigIdea?.oneLiner ?? null,
      refinedIdeaLength: skipResult.bigIdea?.refinedIdea?.length ?? 0,
      refinedIdeaPreview: skipResult.bigIdea?.refinedIdea?.slice(0, 120) ?? null,
      hasProblem: !!skipResult.bigIdea?.problem,
      hasSolution: !!skipResult.bigIdea?.solution,
      hasTargetAudience: !!skipResult.bigIdea?.targetAudience,
      source: cumulative ? (liveObj ? "cumulative+live" : "cumulative") : liveObj ? "live" : "result-state-fallback",
    });

    trackOnboardingChatSkipClicked({
      messageCount: currentMessages.filter((m) => m.role === "user").length,
    });
    trackLandingConversionEvent("skip", skipResult);

    setResult(skipResult);
    setIsReady(true);
  }, [trackLandingConversionEvent, accumulatedResultRef, messagesRef, resultRef, setResult]);

  const handleObjectFinish = useCallback(
    ({ object }: { object: UseObjectReturn["object"] }) => {
      // eslint-disable-next-line no-console -- intentional onboarding diagnostic log
      console.log("[IdeaChatInput] onFinish called, object:", object);

      // Guard: if messages were cleared by reset, ignore this stale onFinish callback.
      // When stop() is called during reset, onFinish fires asynchronously after messages
      // are already emptied. Without this guard, the assistant message gets re-added.
      if (messagesRef.current.length === 0 && !pendingSkipRef.current) {
        setStreamingMessage("");
        streamingMessageRef.current = "";
        return;
      }
      setFactsRefreshSignal((n) => n + 1);

      // Guard: if timeout handler already committed this turn's message and
      // dispatched any pending actions, skip to avoid duplicating the assistant
      // message in history (which causes the LLM to repeat its previous response).
      if (turnMessageCommittedRef.current) {
        turnMessageCommittedRef.current = false;
        setStreamingMessage("");
        streamingMessageRef.current = "";
        return;
      }

      // Use ref as fallback since object?.message may be undefined by the time onFinish is called.
      // Whitespace-only prose counts as no prose: it is truthy, so it would pass
      // the empty-turn guard below and commit a blank assistant bubble — the same
      // silence that guard exists to catch. Normalising here keeps every
      // downstream emptiness check agreeing; the unmodified string is what gets
      // committed, so real internal formatting survives.
      const streamedMessage = object?.message || streamingMessageRef.current;
      const finalMessage = streamedMessage?.trim() ? streamedMessage : "";

      // Clear streaming UI state
      setStreamingMessage("");
      streamingMessageRef.current = "";

      // Handle pending skip
      if (pendingSkipRef.current) {
        if (finalMessage) {
          commitMessages([...messagesRef.current, { role: "assistant", content: finalMessage }]);
        }
        // Flush before useObject resets its internal state on the next render,
        // and keep the reactive mirror in sync for completion/persistence logic.
        const mergedResult = flushToAccumulator(object?.result as PartialOnboardingResult | undefined);
        if (mergedResult) setStableResult(mergedResult);
        setPendingSkip(false);
        // Defer to next tick so state updates from onFinish settle
        setTimeout(() => {
          executeSkipAction();
        }, 0);
        return;
      }

      // A failed generation reaches here as a SUCCESSFUL empty stream: the
      // AI SDK ends the route's textStream cleanly when the provider call
      // fails, so nothing throws and `onError` never fires. Emptiness is the
      // only signal the client gets.
      //
      // Both halves must be empty: the model can answer entirely in structured
      // fields, so a result-only turn is a success, not a silence.
      if (!finalMessage && !object?.result) {
        setTurnFailed(true);
        trackOnboardingChatError({
          errorMessage: "empty_stream",
          messageCount: messagesRef.current.filter((m) => m.role === "user").length,
        });
        // `transient` (level "warning"), not an error: the route's own
        // capture is the authoritative record of WHY generation failed. This
        // one only records that a founder actually SAW the silence, which the
        // server cannot know.
        captureClientError(new Error("Onboarding chat finished with an empty turn"), {
          feature: "onboarding-chat",
          errorType: "emptyTurn",
          impact: "transient",
        });
        handBackQueuedAnswer();
        markTurnFinished(true);
        return;
      }

      // Normal path: add assistant message to history.
      // Sync messagesRef immediately so a fast user submit reads correct history.
      if (finalMessage) {
        // eslint-disable-next-line no-console -- intentional onboarding diagnostic log
        console.log("[IdeaChatInput] Final message:", finalMessage);
        commitMessages([...messagesRef.current, { role: "assistant", content: finalMessage }]);
        // This turn's own read-due header (set by readDueFetch before finish)
        // is what says the reply carried the materials read.
        const readDue = lastTurnReadDueRef.current;
        if (program && !programFirstReadTrackedRef.current && readDue && readDue.size > 0) {
          programFirstReadTrackedRef.current = true;
          trackProgramFirstReadShown({ program, readKind: "materials_read" });
        }
      }
      markTurnFinished();

      // Completion detection is handled by the useEffect watching object?.result?.status
      // which now fires during streaming (no !isLoading guard). No backup needed here.
    },
    [executeSkipAction, markTurnFinished, handBackQueuedAnswer, program, commitMessages, flushToAccumulator, messagesRef, setStableResult, setStreamingMessage, streamingMessageRef, lastTurnReadDueRef],
  );

  const handleObjectError = useCallback(
    (err: Error) => {
      console.error("Chat error:", err);
      setStreamingMessage("");
      streamingMessageRef.current = "";
      // Reset timeout tracking
      streamStartTimeRef.current = null;
      lastChunkTimeRef.current = null;
      setHasTimedOut(false);

      setTurnFailed(true);

      // Track error in analytics
      const currentMessages = messagesRef.current;
      trackOnboardingChatError({
        errorMessage: err instanceof Error ? err.message : "Unknown error",
        messageCount: currentMessages.filter((m) => m.role === "user").length,
      });

      // Capture via captureClientError, impact:"transient" (maps to the same
      // WARNING level as the previous raw call): the server route's
      // captureError is the authoritative alerting source for onboarding
      // failures (blocking/transient by abort state), so an error-level
      // client copy would double-count the fatal/error alert metrics and
      // fire the new-issue alert for events the server already classified.
      // Routing through the helper also lets a real dropped connection here
      // (onError receives the SDK's raw fetch failure) get recognized and
      // tagged networkDrop instead of paging as this call site's own level.
      captureClientError(err, {
        feature: "onboarding-chat",
        errorType: "useObject",
        impact: "transient",
        contexts: {
          chatState: {
            messageCount: currentMessages.filter((m) => m.role === "user").length,
            hadStreamingMessage: !!streamingMessageRef.current,
          },
        },
      });
      handBackQueuedAnswer();
      markTurnFinished(true);
    },
    [markTurnFinished, handBackQueuedAnswer, messagesRef, setStreamingMessage, streamingMessageRef],
  );

  // @ai-sdk/react builds a new `submit` every render, and a stream calls the
  // onFinish/onError of the render whose submit started it. Pairing each
  // render's callbacks with that render's submit scopes them to one
  // generation: a stopped (timed-out) stream that finishes late after a newer
  // submit is ignored instead of appending its reply after the new answer.
  // Relies on SDK internals pinned by the "@ai-sdk/react useObject contract"
  // test in src/tests/components.ideaChatInput.test.tsx.
  const callbackOwner: { submit: UseObjectReturn["submit"] | null } = { submit: null };
  const isSupersededGeneration = () =>
    callbackOwner.submit !== null &&
    callbackOwner.submit !== activeSubmitRef.current &&
    startedSubmitsRef.current.has(callbackOwner.submit);
  // Records which sources this turn's own reply was told to read, straight off
  // the response header — the one signal a mid-turn materials-poll race can't
  // corrupt (LEV-1969, see lastTurnReadDueRef). The owning generation is
  // captured at CALL time and re-checked when the response lands: a stopped
  // (timed-out) turn's late response must not clobber a newer turn's header,
  // same staleness contract as isSupersededGeneration above.
  const readDueFetch: typeof fetch = useCallback(async (input, init) => {
    const owningSubmit = activeSubmitRef.current;
    const response = await fetch(input, init);
    if (activeSubmitRef.current === owningSubmit) {
      lastTurnReadDueRef.current = parseReadDueHeader(response.headers.get("x-lev-read-due"));
    }
    return response;
  }, [lastTurnReadDueRef]);
  const { object, submit, isLoading, error, stop, clear } = useObject({
    api: "/api/onboarding/chat",
    schema: OnboardingResponseSchema,
    fetch: readDueFetch,
    onFinish: (event) => {
      if (isSupersededGeneration()) return;
      handleObjectFinish(event);
    },
    onError: (err) => {
      if (isSupersededGeneration()) return;
      handleObjectError(err);
    },
  });
  callbackOwner.submit = submit;

  // Sync refs for current values (used inside timeout handler to avoid stale closures)
  objectRef.current = object;
  submitRef.current = submit;

  // Stream partial message updates to UI and capture in ref
  useEffect(() => {
    if (object?.message) {
      setStreamingMessage(object.message);
      streamingMessageRef.current = object.message;
      // Update heartbeat timestamp on each chunk
      lastChunkTimeRef.current = Date.now();
    }
  }, [object?.message, setStreamingMessage, streamingMessageRef]);

  // Keep heartbeat alive during result streaming (object.message stops
  // changing once the message field completes, but result keeps streaming).
  useEffect(() => {
    if (object?.result != null) {
      lastChunkTimeRef.current = Date.now();
    }
  }, [object?.result]);

  // Maintain a cumulative merged snapshot of the onboarding result across all
  // streaming turns. The LLM is not guaranteed to re-emit every field on every
  // turn, and useObject resets `object` to empty on each new submit, so
  // without this the Skip path would lose data captured in earlier exchanges.
  useEffect(() => {
    const streamed = object?.result as PartialOnboardingResult | undefined;
    if (!streamed) return;
    const merged = flushToAccumulator(streamed);
    if (!merged) return;
    // object?.result is an unstable reference (new object every render from
    // useObject). Use functional setState so React bails out when content is
    // unchanged, preventing infinite re-render loops.
    setStableResult(prev => {
      if (prev && JSON.stringify(prev) === JSON.stringify(merged)) return prev;
      return merged;
    });
  }, [object?.result, flushToAccumulator, setStableResult]);

  // Timeout recovery: Reset stuck loading state
  // useObject can get stuck in isLoading=true when streams fail silently
  // This effect runs a periodic check while loading and force-recovers if:
  // 1. Overall timeout exceeded (45s), OR
  // 2. Heartbeat timeout (15s without new data during active streaming)
  useEffect(() => {
    // Handle timeout recovery logic
    function handleStreamTimeout(reason: "timeout" | "heartbeat") {
      // Clear interval
      if (timeoutCheckIntervalRef.current) {
        clearInterval(timeoutCheckIntervalRef.current);
        timeoutCheckIntervalRef.current = null;
      }

      // Stop the stream
      stop();

      // Reset refs
      streamStartTimeRef.current = null;
      lastChunkTimeRef.current = null;

      // Clear streaming message (preserve any partial message in history).
      // Whitespace-only prose counts as no reply (mirrors handleObjectFinish
      // above): otherwise it is truthy, commits a blank assistant bubble,
      // and skips the retry offer below for a turn the founder never saw.
      const partialMessage = streamingMessageRef.current.trim()
        ? streamingMessageRef.current
        : "";
      if (partialMessage) {
        // Save partial response as assistant message so user doesn't lose context.
        // Also mark as committed so onFinish (triggered by stop()) doesn't add it again.
        turnMessageCommittedRef.current = true;
        commitMessages([...messagesRef.current, { role: "assistant", content: partialMessage }]);
      }
      setStreamingMessage("");
      streamingMessageRef.current = "";

      // Mark as timed out to trigger re-render with ready state
      setHasTimedOut(true);

      // Process any pending actions (skip error reporting when user initiated an action)
      if (pendingSkipRef.current) {
        const mergedTimeout = flushToAccumulator(objectRef.current?.result as PartialOnboardingResult | undefined);
        if (mergedTimeout) setStableResult(mergedTimeout);
        setPendingSkip(false);
        setTimeout(() => {
          executeSkipAction();
        }, 0);
        return;
      }
      markTurnFinished();

      // A queued answer goes out as soon as this turn is released, so a
      // "try again" toast would contradict what the founder sees next.
      // With no partial reply on screen this is a turn with no reply: the
      // inline retry resends it. A committed partial reply is not retried.
      if (queuedAnswerRef.current === null) {
        if (!partialMessage) {
          setTurnFailed(true);
        } else {
          const errorMsg = reason === "heartbeat"
            ? "Connection was lost. Please try again."
            : "Response took too long. Please try again.";
          toast.error(errorMsg);
        }
      }

      // Track error in analytics
      const errorMessage = `Stream ${reason}: ${reason === "heartbeat" ? "no data" : "exceeded limit"}`;
      trackOnboardingChatError({
        errorMessage,
        messageCount: messagesRef.current.filter((m) => m.role === "user").length,
      });

      // Capture via captureClientError, impact:"transient" (same WARNING
      // level as the previous raw call): stop() aborts the request, which
      // the server route records as transient — an error-level client copy
      // would violate the "transient never alerts" guarantee and
      // double-count the fatal/error metrics. Routing through the helper
      // also tags a drop hiding behind this timeout as networkDrop.
      captureClientError(new Error(`Onboarding chat stream ${reason}`), {
        feature: "onboarding-chat",
        errorType: "stream-timeout",
        impact: "transient",
        tags: { timeoutReason: reason },
        contexts: {
          streamState: {
            reason,
            hadPartialMessage: !!partialMessage,
            messageCount: messagesRef.current.filter((m) => m.role === "user").length,
          },
        },
      });
    }

    if (isLoading) {
      // Mark stream start time if not set
      if (!streamStartTimeRef.current) {
        streamStartTimeRef.current = Date.now();
        lastChunkTimeRef.current = Date.now();
      }

      // Start periodic timeout check
      timeoutCheckIntervalRef.current = setInterval(() => {
        const now = Date.now();
        const streamStart = streamStartTimeRef.current || now;
        const lastChunk = lastChunkTimeRef.current || now;

        const overallElapsed = now - streamStart;
        const heartbeatElapsed = now - lastChunk;

        // Check overall timeout
        if (overallElapsed >= STREAM_TIMEOUT_MS) {
          // eslint-disable-next-line no-console -- surfaces silent stream-timeout recovery in the browser console
          console.warn("[IdeaChatInput] Stream timeout: overall timeout exceeded", {
            elapsed: overallElapsed,
            timeout: STREAM_TIMEOUT_MS,
          });
          handleStreamTimeout("timeout");
          return;
        }

        // Check heartbeat only if we've received at least one chunk (streaming started)
        if (streamingMessageRef.current && heartbeatElapsed >= HEARTBEAT_TIMEOUT_MS) {
          // eslint-disable-next-line no-console -- surfaces silent stream-timeout recovery in the browser console
          console.warn("[IdeaChatInput] Stream timeout: no data received", {
            elapsed: heartbeatElapsed,
            timeout: HEARTBEAT_TIMEOUT_MS,
          });
          handleStreamTimeout("heartbeat");
          return;
        }
      }, 1000);
    } else {
      // Reset timeout tracking when not loading
      streamStartTimeRef.current = null;
      lastChunkTimeRef.current = null;
      setHasTimedOut(false);
      if (timeoutCheckIntervalRef.current) {
        clearInterval(timeoutCheckIntervalRef.current);
        timeoutCheckIntervalRef.current = null;
      }
    }

    // Cleanup function - always return to maintain consistent behavior
    return () => {
      if (timeoutCheckIntervalRef.current) {
        clearInterval(timeoutCheckIntervalRef.current);
        timeoutCheckIntervalRef.current = null;
      }
    };
  }, [isLoading, stop, executeSkipAction, markTurnFinished, commitMessages, flushToAccumulator, messagesRef, setStableResult, setStreamingMessage, streamingMessageRef]);

  // Detect completion via effect — fires when the current turn's status streams
  // in as "complete". Field-completeness checks use stableResult (the cumulative
  // snapshot that persists across turns) so data from earlier exchanges counts.
  // IMPORTANT: Ignore "complete" status if the message ends with a question
  const buildResolveInFlightRef = useRef(false);
  // The gate's verdict on the latest turn: true once it accepts a "complete"
  // (passed the trailing-question check). The single source for
  // isAwaitingFounderReply; cleared when a new turn is sent.
  const completionAcceptedRef = useRef(false);
  const buildResolvePendingRef = useRef<{ snapshot: OnboardingResult | null } | null>(null);
  // Live gate flags for the async resolve's fire-time re-check (deferred-write
  // rule: deferred UI writes re-check their conditions when they fire, not
  // only when scheduled). turnComplete mirrors the SCHEDULING conditions too
  // (PR #688 review): the composer stays interactive while the resolve's GET
  // is in flight, so the founder can submit another message — the stale
  // resolver must not pop the build CTA over the newer streaming turn.
  // turnComplete still requires !isLoading — it guards only the SERVER-FETCH
  // path; the local fast path fires synchronously and needs no fire-time check.
  const turnComplete =
    !isLoading &&
    object?.result?.status === "complete" &&
    !messageEndsWithQuestion(object?.message || "");
  const buildGateRef = useRef({ isReady, showBuildConfirm, userDeclinedBuild, turnComplete, isBuildingProject, waitingForStream });
  buildGateRef.current = { isReady, showBuildConfirm, userDeclinedBuild, turnComplete, isBuildingProject, waitingForStream };
  // The chat is handing off (CTA up, or Build/Skip under way): no further
  // turn may start, so queued notices are dropped rather than sent.
  const isHandoffActive = useCallback(() => {
    const gate = buildGateRef.current;
    return gate.showBuildConfirm || gate.isReady || gate.isBuildingProject;
  }, []);
  useEffect(() => {
    if (object?.result?.status !== "complete") {
      completionAcceptedRef.current = false;
      return;
    }
    if (isReady || showBuildConfirm || userDeclinedBuild) return;
    completionAcceptedRef.current = !messageEndsWithQuestion(object?.message || "");
    if (!completionAcceptedRef.current) {
      // eslint-disable-next-line no-console -- intentional onboarding diagnostic log
      console.log("[IdeaChatInput] Effect: Ignoring complete status - message ends with question:", (object?.message || "").slice(-50));
      return;
    }
    // Parse-gate suppression: the nudge effect or poll callback keeps this
    // true until the nudge fires or the failed parse clears it. Prevents
    // a same-render race between the nudge effect and this effect.
    if (suppressedCompleteRef.current) return;
    // Materials parse gate: while a deck/site poll is in flight and the
    // founder has not hit the message limit, suppress the CTA so the model
    // can deliver the materials critique before the handoff. The server
    // downgrades "complete" → "partial" in the same scenario; this is the
    // client-side mirror (the client keys the CTA off the STREAMED status).
    // userMessageCount is computed inline because the variable is declared
    // later in the render function (it depends on messages, which is a dep).
    const msgCount = messages.filter((m) => m.role === "user").length;
    if (inFlightParses > 0 && msgCount < MAX_USER_MESSAGES) {
      suppressedCompleteRef.current = true;
      gateWasSuppressedRef.current = true;
      setIsMaterialsSuppressed(true);
      trackOnboardingBuildCtaGated({ outcome: "suppressed" });
      return;
    }
    // Local fast path: the accumulated snapshot already has the four required
    // bigIdea fields — show the CTA NOW, without waiting for the stream tail
    // to finish or issuing a server GET. The isLoading gate that guarded the
    // entire effect was added to prevent one GET per chunk during the stream
    // tail; this path does no GET so the guard is unnecessary.
    if (hasCompleteBigIdeaData(stableResult)) {
      // eslint-disable-next-line no-console -- intentional onboarding diagnostic log
      console.log("[IdeaChatInput] Effect detected complete status with local data, showing build confirmation");
      setResult(mergeWithDefaults(stableResult));
      setShowBuildConfirm(true);
      return;
    }
    // Server-fetch fallback: the local snapshot is incomplete (common for
    // deck-only flows where materials capture is server-side). Must wait for
    // isLoading to go false — the effect's deps churn per chunk while streaming,
    // and each churn would issue a GET, exhausting the rate budget.
    if (isLoading) return;
    // Readiness is resolved against the richest available view: the local
    // snapshot fast-path (handled above), else local merged over the server's
    // canonical result. The local snapshot alone is NOT sufficient — the
    // deterministic materials extraction fills capture server-side and the
    // delta-emitting model stops re-emitting captured fields, so gating
    // locally left deck-only founders with a completed chat and no CTA
    // (2026-08-20 eval).
    //
    // One resolver at a time, NO cancellation on dep change: the deps churn
    // every render while snapshots persist, so an effect-cleanup cancel
    // starves the fetch forever (every in-flight resolve died before
    // landing — observed live). Triggers that arrive mid-flight park their
    // snapshot in buildResolvePendingRef and the resolver loop drains the
    // LATEST one — a bare skip would eat the final (complete) trigger.
    // Staleness is handled at FIRE time by re-checking the live gate flags.
    buildResolvePendingRef.current = { snapshot: stableResult };
    if (buildResolveInFlightRef.current) return;
    buildResolveInFlightRef.current = true;
    void (async () => {
      try {
        while (buildResolvePendingRef.current) {
          const { snapshot } = buildResolvePendingRef.current;
          buildResolvePendingRef.current = null;
          const resolved = await resolveCompletedOnboardingResult(snapshot);
          if (!resolved) continue;
          const live = buildGateRef.current;
          // turnComplete re-checks the SCHEDULING conditions live (PR #688
          // review): if the founder submitted another message while the GET
          // was pending, the newer turn owns the conversation — drop this
          // resolve; its completion re-triggers the effect with fresh data.
          if (
            !isMountedRef.current ||
            !live.turnComplete ||
            live.isReady ||
            live.showBuildConfirm ||
            live.userDeclinedBuild
          ) {
            return;
          }
          // eslint-disable-next-line no-console -- intentional onboarding diagnostic log
          console.log("[IdeaChatInput] Effect detected complete status, showing build confirmation");
          setResult(resolved);
          setShowBuildConfirm(true);
          return;
        }
      } finally {
        buildResolveInFlightRef.current = false;
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- inFlightParses and messages gate the parse suppression path
  // isMaterialsSuppressed is a dep so a release (failed parse, or a nudge that
  // could not be sent) re-evaluates the gate and lets the CTA through.
  }, [object, stableResult, isReady, showBuildConfirm, userDeclinedBuild, isLoading, turnComplete, inFlightParses, messages, isMaterialsSuppressed, setResult, gateWasSuppressedRef, setIsMaterialsSuppressed, suppressedCompleteRef]);

  // Derive status for UI compatibility
  // hasTimedOut overrides isLoading to allow recovery after timeout
  const status = isLoading && !hasTimedOut ? "streaming" : "ready";

  const clearIntroDelay = useCallback(() => {
    if (introDelayTimeoutRef.current) {
      clearTimeout(introDelayTimeoutRef.current);
      introDelayTimeoutRef.current = null;
    }
  }, []);

  const clearIntroStream = useCallback(() => {
    if (introIntervalRef.current) {
      clearInterval(introIntervalRef.current);
      introIntervalRef.current = null;
    }
  }, []);

  const showFullIntro = useCallback(() => {
    clearIntroDelay();
    clearIntroStream();
    setIntroStreamedContent(LEV_GREETING_DETAILS);
    setIntroState("done");
  }, [clearIntroDelay, clearIntroStream]);

  // Start the intro automatically after a short idle delay on fresh-load states only.
  useEffect(() => {
    if (hideGreeting || (introState !== "idle" && introState !== "waiting") || hasStartedRef.current || messages.length > 0 || showRestorePrompt) {
      clearIntroDelay();
      if (showRestorePrompt && introState === "waiting") {
        setIntroState("idle");
      }
      return;
    }

    if (introState === "idle") {
      setIntroState("waiting");
    }

    introDelayTimeoutRef.current = setTimeout(() => {
      setIntroState("streaming");
    }, INTRO_STREAM_DELAY_MS);

    return clearIntroDelay;
  }, [hideGreeting, introState, messages.length, showRestorePrompt, clearIntroDelay]);

  // Stream markdown-safe intro frames so the fake animation never renders broken markdown.
  useEffect(() => {
    if (introState !== "streaming") {
      clearIntroStream();
      return;
    }

    let frameIndex = 0;
    setIntroStreamedContent(LEV_GREETING_DETAILS_STREAM_FRAMES[frameIndex] ?? "");

    introIntervalRef.current = setInterval(() => {
      frameIndex += 1;

      const nextFrame = LEV_GREETING_DETAILS_STREAM_FRAMES[frameIndex];
      if (!nextFrame) {
        clearIntroStream();
        setIntroState("done");
        return;
      }

      setIntroStreamedContent(nextFrame);

      if (frameIndex === LEV_GREETING_DETAILS_STREAM_FRAMES.length - 1) {
        clearIntroStream();
        setIntroState("done");
      }
    }, INTRO_STREAM_INTERVAL_MS);

    return clearIntroStream;
  }, [introState, clearIntroStream]);

  useEffect(() => {
    return () => {
      clearIntroDelay();
      clearIntroStream();
    };
  }, [clearIntroDelay, clearIntroStream]);

  // Set by the mount check when the sign-in resume marker is present; the
  // effect after handleRestore consumes it once savedChat is in state.
  const autoRestorePendingRef = useRef(false);
  // Set once a "sign-in-resume" restore has run. STATE, not a ref: the
  // Clerk-gated effect below must re-run when this flips even if isLoaded/
  // isSignedIn are unchanged (e.g. Clerk was already signed in on mount), and
  // an effect only re-runs when one of ITS OWN deps changes — a ref mutation
  // is invisible to that check.
  const [signInResumeRestored, setSignInResumeRestored] = useState(false);

  // Check for saved chat on mount (restore from backup if main keys were consumed)
  useEffect(() => {
    restoreOnboardingFromBackup();
    const saved = peekOnboardingChat();
    if (saved && saved.messages.length > 0) {
      // Only offer restore if chat has at least one user message (sanity check).
      // A reset race condition can save a chat with only an assistant message — skip those.
      const hasUserMessage = saved.messages.some((m) => m.role === "user");
      if (!hasUserMessage) {
        consumeOnboardingChat(); // Clear corrupted state
        return;
      }
      // Show restore prompt for any saved chat (including complete/skipped)
      // This allows user to click Back from sign-up and restore conversation
      // Chat gets cleared after successful project creation in onboarding/complete
      setSavedChat(saved);
      if (peekResumeChat()) {
        autoRestorePendingRef.current = true;
      } else {
        setShowRestorePrompt(true);
      }
    }
  }, []);

  // Handle restoring saved conversation
  const handleRestore = useCallback((trigger: "prompt" | "sign-in-resume") => {
    if (savedChat) {
      clearIntroDelay();
      clearIntroStream();
      setIntroState("idle");
      setIntroStreamedContent("");
      // Reset the chat, continuing any partial result from where the founder
      // left off.
      restoreConversationState(savedChat, {
        rubricTypeDirty:
          !!savedChat.result &&
          (savedChat.result.rubricType === "bootstrap" ||
            !!peekOnboardingSessionBackup()?.rubricTypeDirty),
      });
      hasStartedRef.current = true;
      setIsExpanded(true);
      setShowRestorePrompt(false);

      // Track restoration. A sign-in-resume restore repeats on every mount
      // (LandingHero remounts once Clerk resolves) but must count once per
      // marker, not once per mount — the claim below is that gate.
      if (trigger === "prompt" || claimResumeRestoreTracking()) {
        trackOnboardingChatRestored({
          messageCount: savedChat.messages.length,
          trigger,
        });
      }
      if (trigger === "sign-in-resume") {
        setSignInResumeRestored(true);
      }
    }
  }, [savedChat, clearIntroDelay, clearIntroStream, restoreConversationState]);

  // Sign-in resume (LEV-1925): a founder who signed in mid-chat gets the chat
  // back without the "Resume" prompt. The marker is only PEEKED here, never
  // cleared: LandingHero remounts this component once Clerk knows the user,
  // and the remount must restore again. The effect below clears it once that
  // remount has actually happened; a new send or a reset also clear it early.
  useEffect(() => {
    if (!autoRestorePendingRef.current || !savedChat) return;
    autoRestorePendingRef.current = false;
    handleRestore("sign-in-resume");
  }, [savedChat, handleRestore]);

  // A stale marker would auto-restore the chat again on a later visit — send
  // nothing, open a project, come back within the 10-minute window — so it
  // must go once a sign-in-resume restore is truly done. "Done" means Clerk
  // itself confirms the sign-in, not just that handleRestore ran: the FIRST
  // mount (still anonymous) can restore too, and clearing there would race
  // the LandingHero remount that still needs to see the marker.
  useEffect(() => {
    if (!signInResumeRestored || !isClerkLoaded || !isSignedIn) return;
    setSignInResumeRestored(false);
    clearResumeChat();
  }, [signInResumeRestored, isClerkLoaded, isSignedIn]);

  // All messages including the greeting
  const allMessages = useMemo(() => {
    if (hideGreeting) return messages;
    const introMessages = introState === "idle" || !introStreamedContent
      ? []
      : [{ role: "assistant" as const, content: introStreamedContent }];

    return [LEV_GREETING_MESSAGE, ...introMessages, ...messages];
  }, [hideGreeting, introState, introStreamedContent, messages]);

  const committedMessages = allMessages;

  // Coalesce sessionStorage writes during streaming (330-430 per deck turn without this).
  // Non-streaming changes (user message commit, restore) write immediately.
  const { schedule: scheduleSessionPersist, cancel: cancelSessionPersist } = useCoalescedPersist({ isStreaming: isLoading });

  // Save chat to sessionStorage on every change (for restore on reload).
  // Uses stableResult (cumulative across turns) so a new turn never clobbers
  // data captured earlier. Falls back to the completed `result` state.
  useEffect(() => {
    if (messages.length === 0) return;
    const candidate = result ?? stableResult ?? undefined;
    const resultToSave = candidate && candidate.status === "complete" && !hasCompleteBigIdeaData(candidate)
      ? { ...candidate, status: "partial" as const }
      : candidate;
    // Capture values — the closure must capture the snapshot AT this render, not later.
    const capturedMessages = messages;
    const capturedResult = resultToSave;
    const capturedRubricTypeDirty = rubricTypeDirtyRef.current;
    const capturedResultSource = result ? "result-state" : stableResult ? "stable-result" : "none";
    scheduleSessionPersist(() => {
      // log is inside the actual write so one log line == one real sessionStorage write.
      console.log("[IdeaChatInput] Persisting onboarding chat snapshot", {
        messageCount: capturedMessages.length,
        hasResultToSave: !!capturedResult,
        status: capturedResult?.status ?? null,
        journeyStage: capturedResult?.journey?.stage ?? null,
        oneLiner: capturedResult?.bigIdea?.oneLiner ?? null,
        refinedIdeaLength: capturedResult?.bigIdea?.refinedIdea?.length ?? 0,
        refinedIdeaPreview: capturedResult?.bigIdea?.refinedIdea?.slice(0, 120) ?? null,
        resultSource: capturedResultSource,
      });
      saveOnboardingChat({
        messages: capturedMessages,
        ...(capturedResult && { result: capturedResult }),
      });
      // Full-session conversion backup: the sole idea source if the server
      // session cookie is missing at claim time (mint rate-limited / blocked).
      // Only meaningful once a result exists; the completion resolver ignores a
      // backup without minimum idea data.
      if (capturedResult) {
        saveOnboardingSessionBackup({
          result: capturedResult,
          messages: capturedMessages,
          rubricTypeDirty: capturedRubricTypeDirty,
        });
      }
    });
  }, [messages, result, stableResult, isLoading, scheduleSessionPersist, rubricTypeDirtyRef]);

  // Fetch debug info (system prompt, model) once on mount in development
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    fetch("/api/onboarding/chat")
      .then((res) => res.json())
      .then((data) => {
        aiDebug.updateDebugState({
          systemPrompt: data.systemPrompt || "",
          modelId: data.modelId || "",
        });
      })
      .catch(() => {
        // Debug endpoint not available, ignore
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only fetch once on mount
  }, []);

  // Update debug state when messages or status changes
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    aiDebug.updateDebugState({
      messages: toDebugMessages(messages),
      structuredOutput: result ?? stableResult ?? null,
      debugInfo: {
        status,
        userMessageCount: messages.filter((m) => m.role === "user").length,
        hasResult: !!result,
        isReady,
      },
    });
  }, [messages, status, result, isReady, stableResult, aiDebug]);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const newHeight = Math.min(
        Math.max(textarea.scrollHeight, MIN_TEXTAREA_HEIGHT),
        MAX_TEXTAREA_HEIGHT,
      );
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  // Auto-scroll policy (LEV-1296): scroll to the bottom on sends and when the
  // build-confirm / building CTAs appear, but while the AI response streams
  // only follow until the top of the streaming bubble would leave view, then
  // hold so the user can read from the beginning. Manual scroll always wins.
  const { controller: chatScrollController, onScroll: handleChatScroll } =
    useChatStreamScroll();

  const prevScrollTickRef = useRef<{
    streaming: boolean;
    showBuildConfirm: boolean;
    isBuildingProject: boolean;
    turnFailed: boolean;
  } | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const prev = prevScrollTickRef.current;
    const streaming = status === "streaming";
    prevScrollTickRef.current = { streaming, showBuildConfirm, isBuildingProject, turnFailed };

    const ctaAppeared =
      (showBuildConfirm && !prev?.showBuildConfirm) ||
      (isBuildingProject && !prev?.isBuildingProject);
    // CTAs can stream in mid-turn while the founder reads from the top —
    // reveal them only when auto-following; a manual scroll keeps priority
    // (PR #537 review, P1).
    if (ctaAppeared) return chatScrollController.scrollToBottomUnlessOverridden(el);
    // The failed-turn notice replaces the Thinking indicator but is taller,
    // so it can sit below the fold on appearance; reveal it without
    // overriding a manual scroll, same as a CTA appearing.
    if (turnFailed && !prev?.turnFailed) {
      return chatScrollController.scrollToBottomUnlessOverridden(el);
    }
    if (streaming && !prev?.streaming) {
      return chatScrollController.resetAndScrollToBottom(el);
    }
    if (streaming) {
      return chatScrollController.followStreamTick(
        el,
        el.querySelector(`[${STREAM_ANCHOR_ATTR}]`),
      );
    }
    // Stream just ended — leave the pane where the reader is.
    if (prev?.streaming) return;
    return chatScrollController.scrollForIdleUpdate(el);
  }, [
    chatScrollController,
    allMessages,
    status,
    streamingMessage,
    showBuildConfirm,
    isBuildingProject,
    turnFailed,
  ]);

  // Focus the input once on initial load so users can start typing immediately.
  // preventScroll: a program lander's hero sits above this card, and a
  // scrolling focus would land the founder below it.
  useEffect(() => {
    textareaRef.current?.focus({ preventScroll: true });
  }, []);

  // A starter that must replace an existing conversation waits here for the
  // founder's Reset confirm. Every dialog close other than a successful
  // Reset MUST clear it, or a later plain Reset would send it.
  // fixed on 2026-10-30: LEV-2827
  const pendingStarterRef = useRef<{ text: string; source: LandingStarterSource } | null>(null);
  // Set by handleReset only after a successful reset. Sent from an effect,
  // not inline: handleSubmitRef must hold the closure rendered with the
  // cleared conversation, or the send sees the old turn state.
  const [starterAfterReset, setStarterAfterReset] = useState<
    { text: string; source: LandingStarterSource } | null
  >(null);
  const canResetRef = useRef(true);
  const sendRequestText = sendRequest?.text;
  const sendRequestNonce = sendRequest?.nonce;
  const sendRequestSource = sendRequest?.source ?? "capability_card";
  useEffect(() => {
    if (sendRequestText === undefined) return;
    // A saved chat offered for resume still owns the server session cookie,
    // so starting fresh needs the same detach as an on-screen conversation.
    if (hasStartedRef.current || showRestorePromptRef.current) {
      // Same gate as the Reset button: the build CTA can still be reset; only
      // a Build/Skip already navigating away cannot.
      const gate = buildGateRef.current;
      if (gate.isReady || gate.isBuildingProject || !canResetRef.current) {
        if (hasStartedRef.current) setIsExpanded(true);
        return;
      }
      pendingStarterRef.current = { text: sendRequestText, source: sendRequestSource };
      setShowResetConfirm(true);
      return;
    }
    handleSubmitRef.current(sendRequestText, { source: sendRequestSource });
  }, [sendRequestText, sendRequestNonce, sendRequestSource]);

  useEffect(() => {
    if (!starterAfterReset) return;
    setStarterAfterReset(null);
    handleSubmitRef.current(starterAfterReset.text, { source: starterAfterReset.source });
  }, [starterAfterReset]);

  const closeResetConfirm = useCallback(() => {
    pendingStarterRef.current = null;
    setShowResetConfirm(false);
  }, []);

  // Single owner of composer focus (QA 2026-08-11): disabling the textarea
  // (file parse, pending skip, submit) makes the browser drop focus to
  // <body>, and nothing restored it outside the expanded modal — the founder
  // had to click back into the input after every disable. Whenever the
  // composer returns to an interactive state (including mid-stream and the
  // expand-portal remount), put focus back so it is always ready for input.
  // Deliberately NOT gated on isLoading: the textarea stays enabled while a
  // reply streams (Send waits for Lev's first token). isLoading stays in the deps so the
  // stream-end flip re-runs the refocus (pre-existing behavior).
  useEffect(() => {
    if (
      showRestorePrompt ||
      showBuildConfirm ||
      showResetConfirm ||
      waitingForStream ||
      isBuildingProject ||
      isSubmitting ||
      (isParsingFile && !isUploadingAnnouncedDeck) ||
      pendingSkip ||
      queuedAnswer !== null ||
      // A minimized, started chat must stay minimized: minimizing unmounts the
      // portal and drops focus to <body>, the same "focus went missing" signal
      // this effect exists to repair. Refocusing here fires handleExpand's
      // onFocus and reopens the chat within one frame. fixed on 2026-09-26: LEV-1925
      (hasStartedRef.current && !isExpanded)
    ) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const el = textareaRef.current;
      // Fire-time re-check (deferred-UI rule): a disable flag may have
      // flipped back between scheduling and this frame.
      if (!el || el.disabled) return;
      // Never yank focus from a control the founder deliberately moved to
      // (Reset/Skip/upload buttons); a disable drops focus to <body>, which
      // is the case this exists to repair.
      const active = document.activeElement;
      if (active && active !== document.body && active !== el) return;
      // preventScroll: the sticky composer is already in view — a plain
      // focus() could fight the stream auto-scroll controller.
      el.focus({ preventScroll: true });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [
    isExpanded,
    isLoading,
    showRestorePrompt,
    showBuildConfirm,
    showResetConfirm,
    waitingForStream,
    isBuildingProject,
    isSubmitting,
    isParsingFile,
    isUploadingAnnouncedDeck,
    pendingSkip,
    queuedAnswer,
  ]);

  // Lock body scroll when expanded
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isExpanded]);

  const userMessageCount = messages.filter((m) => m.role === "user").length;
  const hasStarted = hasStartedRef.current || messages.length > 0;
  const holdsFounderInput =
    hasStarted || isParsingFile || inFlightParses > 0 || showRestorePrompt;
  useEffect(() => {
    onStartedChange?.(holdsFounderInput);
  }, [holdsFounderInput, onStartedChange]);
  // A program lander's empty chat: the drop zone is the one upload control.
  const isProgramIdle = programConfig !== null && !hasStarted;

  // The program card's last inline height, held by the expanded-chat stand-in
  // so the page under the chat does not jump when the conversation opens.
  const inlineCardRef = useRef<HTMLDivElement>(null);
  const [programCardHeight, setProgramCardHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = inlineCardRef.current;
    if (!programConfig || isExpanded || !el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setProgramCardHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [programConfig, isExpanded]);

  // Tells the header's Login link to resume this chat after sign-in
  // (LEV-1925). The home landing publishes "" because null already means
  // "no chat in progress". The cleanup covers both a reset and unmount.
  const setMidChatSlug = useLandingChatPresenceStore((s) => s.setMidChatSlug);
  const hasUserMessage = userMessageCount > 0;
  useEffect(() => {
    if (!hasUserMessage) return;
    setMidChatSlug(resolvePartnerSlug(landingPageSlug ?? "home") ?? "");
    return () => setMidChatSlug(null);
  }, [hasUserMessage, landingPageSlug, setMidChatSlug]);

  const streamPhase: StreamPhase = useMemo(() => {
    if (!isLoading || hasTimedOut) return 'idle';
    if (object?.result != null) return 'streaming-result';
    return 'streaming-message';
  }, [isLoading, hasTimedOut, object?.result]);

  const canReset = !isParsingFile && !pendingSkip;
  canResetRef.current = canReset;
  // Skip needs idea data, not a refinedIdea: the model is told not to
  // re-emit fields the server already captured (a deck read fills
  // refinedIdea server-side), so waiting for it kept Skip disabled for
  // several turns. Skip's own rawIdea fallback covers the rest.
  const canSkip =
    !isParsingFile && !pendingSkip && hasMinimumBigIdeaData(stableResult ?? result);
  // The founder's answer queues (see handleSubmit) from Lev's first visible
  // character. Before that there is nothing on screen to answer. A turn stays
  // atomic either way: nothing reaches submit() until the current turn has
  // finished. useObject's submit() clears `object` in the same batch that
  // sets isLoading, so a new turn never inherits the previous turn's message.
  // A result-only tail (handleObjectFinish supports one) still has no visible
  // prose for the founder to answer against, so both phases require it.
  const canQueueAnswer =
    (streamPhase === 'streaming-result' || streamPhase === 'streaming-message') &&
    !!object?.message?.trim();
  const canSend =
    (streamPhase === 'idle' || canQueueAnswer) &&
    !showBuildConfirm &&
    !pendingSkip &&
    !isAwaitingFirstMint &&
    queuedAnswer === null;
  const isTurnBusy = streamPhase !== 'idle' || isAwaitingFirstMint;
  const streamIdleRef = useRef(!isTurnBusy);
  streamIdleRef.current = !isTurnBusy;
  // Return pressed while the answer cannot queue yet says why instead of
  // doing nothing. Shown only while that is still true.
  const answerMustWait = isTurnBusy && !canQueueAnswer;
  const [answerWaitHint, setAnswerWaitHint] = useState(false);
  // At most one event per turn: answerMustWait flips false at turn end, which
  // is also the only path that clears this, so a founder mashing Enter during
  // one wait doesn't inflate the rate.
  const hintFiredForTurnRef = useRef(false);
  useEffect(() => {
    if (!answerMustWait) {
      setAnswerWaitHint(false);
      hintFiredForTurnRef.current = false;
    }
  }, [answerMustWait]);

  // Progress bar: decreasing increments per question, holds at 95% until COMPLETE → 100%
  const progressPercent = useMemo(() => {
    if (result?.status === "complete" || userDeclinedBuild) {
      return 100;
    }
    let total = 0;
    for (let i = 0; i < userMessageCount && i < PROGRESS_INCREMENTS.length; i++) {
      total += PROGRESS_INCREMENTS[i];
    }
    return total; // max 95% (sum of PROGRESS_INCREMENTS)
  }, [userMessageCount, result?.status, userDeclinedBuild]);

  // Opens a suppressed gate with no nudge, sending its one "released" event.
  // Reads only refs and a stable setter, so the identity is stable.
  const releaseMaterialsGate = useCallback(() => {
    suppressedCompleteRef.current = false;
    setIsMaterialsSuppressed(false);
    trackOnboardingBuildCtaGated({ outcome: "released", wasSuppressed: gateWasSuppressedRef.current });
    gateWasSuppressedRef.current = false;
  }, [gateWasSuppressedRef, setIsMaterialsSuppressed, suppressedCompleteRef]);
  // A held nudge dropped because the chat stops taking turns (Build CTA, Skip).
  const dropHeldNudge = useCallback(() => {
    if (!nudgeHeldRef.current) return;
    nudgeHeldRef.current = false;
    releaseMaterialsGate();
  }, [releaseMaterialsGate, nudgeHeldRef]);

  // Ends the parse gate: posts the nudge, or releases the gate when a nudge
  // no longer applies. Conditions are re-checked here, at fire time.
  // Reads only refs and stable setters so the callback identity is stable.
  const resolveMaterialsGate = useCallback(() => {
    const wasSuppressed = gateWasSuppressedRef.current;
    gateWasSuppressedRef.current = false;
    // Consumed once, here: the exact sources this pending nudge is for,
    // frozen by settleMaterialsParse when the batch went into suppression.
    const targetSources = nudgeTargetSourcesRef.current;
    nudgeTargetSourcesRef.current = new Set();
    // Suppression is cleared HERE, not by handleSubmit: if the submit is a
    // no-op (CTA already up, at the message cap) the gate would otherwise
    // stay closed with no path to reopen it.
    suppressedCompleteRef.current = false;
    setIsMaterialsSuppressed(false);
    const msgCount = messagesRef.current.filter(m => m.role === "user").length;
    const gate = buildGateRef.current;
    // A pending Skip must not receive a synthetic turn: the founder's next
    // turn carries the critique anyway. A Build click (waitingForStream:
    // clicked mid-stream) is also the founder's action: the handoff is under
    // way and must not be interrupted.
    const founderActionPending =
      pendingSkipRef.current ||
      gate.waitingForStream ||
      hasAutoProceedTriggeredRef.current;
    if (founderActionPending || gate.isReady || msgCount >= MAX_USER_MESSAGES) {
      trackOnboardingBuildCtaGated({ outcome: "released", wasSuppressed });
      return;
    }
    // The turn that just ended may already have delivered this read: its own
    // [MATERIALS_READ_DUE] block (mirrored on the response header) is decided
    // at TURN START, before persistTurnToSession's fire-and-forget write — a
    // materials-poll GET mid-turn sees the pre-persist session and can report
    // the same source still due. Re-GETting here would hit that identical
    // race; the header is the only value that was never in question. Nudging
    // anyway would re-open a chat Lev already delivered the read in and closed.
    const lastTurnReadDue = lastTurnReadDueRef.current;
    const readAlreadyDelivered =
      targetSources.size > 0 &&
      lastTurnReadDue !== null &&
      Array.from(targetSources).every((source) => lastTurnReadDue.has(source));
    if (readAlreadyDelivered) {
      trackOnboardingBuildCtaGated({ outcome: "released", wasSuppressed, releaseReason: "read_delivered" });
      return;
    }
    // A visible CTA does not skip the read: a deck dropped at the CTA still
    // earns its critique. The CTA comes down for the read turn, and the
    // build-confirm effect brings it back when that turn completes.
    if (gate.showBuildConfirm) setShowBuildConfirm(false);
    trackOnboardingBuildCtaGated({ outcome: "nudged", wasSuppressed });
    handleSubmitRef.current(MATERIALS_NUDGE_MESSAGE);
  }, [messagesRef, gateWasSuppressedRef, lastTurnReadDueRef, nudgeTargetSourcesRef, setIsMaterialsSuppressed, suppressedCompleteRef]);

  // The only place a landed parse decides between a synthetic nudge and none.
  // While Lev is waiting on the founder, the server's [MATERIALS_READ_DUE]
  // block makes Lev deliver the read in its reply to that answer, so a nudge
  // would only put words in the founder's mouth. MUST run while idle.
  const decideMaterialsNudge = useCallback(() => {
    // The shortened program interview has Lev announce a read still running
    // instead of asking something (programContext.ts confirmOnCanvas). That
    // reply hands nothing to the founder, so the read is owed here.
    // readAnnounced only overrides a "waiting on the founder" verdict: an
    // accepted conclusion or a visible CTA already reaches
    // resolveMaterialsGate, which owns the read-delivered and handoff checks.
    const lastMessage = messagesRef.current[messagesRef.current.length - 1];
    const readAnnounced =
      materialsFastPathRef.current.size > 0 &&
      lastMessage?.role === "assistant" &&
      !asksFounder(lastMessage.content);
    if (
      !readAnnounced &&
      isAwaitingFounderReply(
        messagesRef.current,
        completionAcceptedRef.current,
        buildGateRef.current.showBuildConfirm,
      )
    ) {
      releaseMaterialsGate();
      return;
    }
    resolveMaterialsGate();
  }, [resolveMaterialsGate, releaseMaterialsGate, messagesRef, materialsFastPathRef]);

  // A turn that reaches the server learns of the failure there
  // ([MATERIALS_UNREADABLE]), so the flag is spent by any send (handleSubmit).
  // Otherwise Lev's announcement is followed by a fixed local reply rather
  // than a turn sent in the founder's name. A busy turn defers this to the
  // turn-end dispatcher, which calls it again.
  const tellReadUnreadable = useCallback(() => {
    if (readLostRef.current.size === 0) return;
    if (
      !streamIdleRef.current ||
      queuedAnswerRef.current !== null ||
      queuedNoticeRef.current !== null ||
      finishedTurnSeqRef.current !== handledTurnSeqRef.current
    ) {
      return;
    }
    const history = messagesRef.current;
    const last = history[history.length - 1];
    // Lev's reply to the founder's last message is still owed; its turn end
    // brings the dispatcher back here.
    if (last?.role !== "assistant") return;
    const lost = readLostRef.current;
    readLostRef.current = new Set();
    if (
      asksFounder(last.content) ||
      !isAwaitingFounderReply(history, completionAcceptedRef.current, buildGateRef.current.showBuildConfirm)
    ) {
      return;
    }
    // The deck ask covers a website too: it asks for everything a site says.
    const reply = lost.has("deck") ? PROGRAM_DECK_UNREADABLE_REPLY : PROGRAM_SITE_UNREADABLE_REPLY;
    commitMessages([...history, { role: "assistant", content: reply }]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reads only refs; commitMessages writes through messagesRef
  }, []);

  // Shared settle logic for both deck and site parse polls. Decrements the
  // in-flight count and, when the last parse of the batch finishes, either
  // delivers the read (at least one landed ready: nudge now, or hold it until
  // the turn ends) or releases a suppressed CTA (all failed). `epoch` is the
  // materialsEpochRef value the poll started under.
  const settleMaterialsParse = useCallback((slot: MaterialsSlot, pollOk: boolean, epoch: number, readStillDue?: boolean) => {
    if (!isMountedRef.current || epoch !== materialsEpochRef.current) return;
    setFactsRefreshSignal((n) => n + 1);
    if (!pollOk && materialsFastPathRef.current.has(slot)) {
      materialsFastPathRef.current.delete(slot);
      readLostRef.current.add(slot);
    }
    if (pollOk) {
      anyParseReadyRef.current = true;
      pendingNudgeSourcesRef.current.add(slot);
      if (readStillDue !== false) anyReadStillDueRef.current = true;
    }
    const newCount = Math.max(0, inFlightParsesCountRef.current - 1);
    inFlightParsesCountRef.current = newCount;
    setInFlightParses(newCount);
    if (newCount !== 0) return;
    // The ready flag is per batch: reset it every time the batch drains, not
    // only when a completion was suppressed, or a success from an earlier
    // batch would make a later failed parse look ready.
    const anyReady = anyParseReadyRef.current;
    anyParseReadyRef.current = false;
    const stillDue = anyReadStillDueRef.current;
    anyReadStillDueRef.current = false;
    const batchSources = pendingNudgeSourcesRef.current;
    pendingNudgeSourcesRef.current = new Set();
    const wasSuppressed = suppressedCompleteRef.current;
    if (!anyReady) {
      // Deferred like the nudge below, so a turn finishing in this tick is
      // seen as busy and the dispatcher decides at its end.
      if (readLostRef.current.size > 0) {
        setTimeout(() => {
          if (isMountedRef.current && epoch === materialsEpochRef.current) tellReadUnreadable();
        }, 0);
      }
      if (!wasSuppressed) return;
      suppressedCompleteRef.current = false;
      gateWasSuppressedRef.current = false;
      setIsMaterialsSuppressed(false);
      trackOnboardingBuildCtaGated({ outcome: "released", wasSuppressed });
      return;
    }
    // The read of what did land is owed now, and the server tells the founder
    // about the failed source in that same turn.
    readLostRef.current = new Set();
    // The nudge asks for a read the server still owes; a read the server has
    // already recorded as delivered (readDue confirmed empty) must not be
    // asked for again — release the gate instead so the build-confirm effect
    // brings the CTA back, rather than reopening a chat Lev already concluded.
    if (!stillDue) {
      suppressedCompleteRef.current = false;
      gateWasSuppressedRef.current = false;
      setIsMaterialsSuppressed(false);
      trackOnboardingBuildCtaGated({ outcome: "released", wasSuppressed });
      return;
    }
    // The gate closes in the same tick as inFlightParses drops to 0: with no
    // parse in flight the build-confirm effect would otherwise show the CTA
    // ahead of the read.
    gateWasSuppressedRef.current = wasSuppressed;
    suppressedCompleteRef.current = true;
    setIsMaterialsSuppressed(true);
    nudgeTargetSourcesRef.current = batchSources;
    // Deferred to the next event-loop turn so the inFlightParses state update
    // commits first; the build-confirm effect sees the closed gate and bails.
    setTimeout(() => {
      // Reset, or a founder turn (handleSubmit clears suppression), took over
      // the gate in between: the nudge no longer applies.
      if (!isMountedRef.current || epoch !== materialsEpochRef.current) return;
      if (!suppressedCompleteRef.current) return;
      // A finished turn not yet dispatched, or a queued answer or deck notice,
      // owns the next send: hold so the dispatcher decides after them. While a
      // nudge is held, suppression is on, so the CTA cannot appear first.
      if (
        !streamIdleRef.current ||
        queuedAnswerRef.current !== null ||
        queuedNoticeRef.current !== null ||
        finishedTurnSeqRef.current !== handledTurnSeqRef.current
      ) {
        nudgeHeldRef.current = true;
        return;
      }
      decideMaterialsNudge();
    }, 0);
  }, [decideMaterialsNudge, tellReadUnreadable, anyParseReadyRef, anyReadStillDueRef, gateWasSuppressedRef, inFlightParsesCountRef, materialsEpochRef, materialsFastPathRef, nudgeHeldRef, nudgeTargetSourcesRef, pendingNudgeSourcesRef, readLostRef, setInFlightParses, setIsMaterialsSuppressed, suppressedCompleteRef]);

  // A deck Lev announced (PROGRAM_DECK_FIRST_REPLY) that never reached the
  // server. The server has no failed slot to report, so a LinkedIn ask still
  // open gets a fixed retraction here; one the founder already answered gets
  // tellReadUnreadable's line, through the settle.
  const abandonAnnouncedDeck = useCallback((epoch: number) => {
    if (!isMountedRef.current || epoch !== materialsEpochRef.current) return;
    const last = messagesRef.current[messagesRef.current.length - 1];
    const askStillOpen = last?.role === "assistant" && last.content === PROGRAM_DECK_FIRST_REPLY;
    settleMaterialsParse("deck", false, epoch);
    if (!askStillOpen) return;
    readLostRef.current.delete("deck");
    commitMessages([
      ...messagesRef.current,
      { role: "assistant", content: PROGRAM_DECK_NOT_UPLOADED_REPLY },
    ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- commitMessages writes through messagesRef
  }, [settleMaterialsParse]);

  // Stage a pasted website URL for the server-side parse job (LEV-1431), the
  // same way a deck upload is staged: confirm the session, POST `{ url }`, then
  // poll the `site` slot in the
  // background for analytics + one failure toast. The returned promise settles
  // when the POST has settled (accepted, rejected, or skipped) — NOT when the
  // poll finishes — because handleSubmit holds the chat turn on it: the chat
  // route learns about the site from its own materials read, so the processing
  // marker must be on file before the turn arrives or the model answers without
  // the [MATERIALS_PARSING] block and its hard gate (LEV-1422 review). The
  // founder's message goes out unchanged either way. `host` is released from
  // the client dedupe set on the no-POST, failed-POST, and failed-poll paths
  // so the founder can paste the URL again; the server's per-slot idempotency
  // stays the authority. Resolves true only when the read was queued.
  const requestSiteParse = useCallback(
    async (url: string, host: string): Promise<boolean> => {
      const epoch = materialsEpochRef.current;
      const isCurrentEpoch = () => isMountedRef.current && epoch === materialsEpochRef.current;
      const sessionReady = await ensureSession();
      if (!isCurrentEpoch()) return false;
      if (!sessionReady) {
        // No usable session: the URL is plain text, nothing to stage.
        parsedSiteHostsRef.current.delete(host);
        return false;
      }

      const abortController = new AbortController();
      siteParseAbortRef.current = abortController;
      const { signal } = abortController;

      const postResult = await postMaterialsParse({ url }, signal);
      if (signal.aborted || !isCurrentEpoch()) return false;
      if (!postResult.ok) {
        parsedSiteHostsRef.current.delete(host);
        // 4xx outcomes (bad URL, rate limit) are product events, not faults;
        // the server captures its own 5xx. Only a network-level failure is
        // invisible server-side, so that one is captured here.
        if (postResult.status === null) {
          captureClientError(
            new Error(`Onboarding site parse POST failed: ${postResult.reason}`),
            {
              feature: "onboarding-chat",
              errorType: "site-parse",
              impact: "degraded",
              contexts: { siteParse: { phase: postResult.phase } },
            },
          );
        }
        trackMaterialsParsed({ sourceType: "site", outcome: "failure" });
        trackOnboardingMaterialsParseFailed({
          sourceType: "site",
          phase: postResult.phase,
          httpStatus: postResult.status,
        });
        // Same founder-facing notice as the poll-failure path below: a rejected
        // POST (rate limit, timeout) previously failed in silence, so the
        // founder assumed their site was read when it never entered the queue.
        // Deliberately UNGATED, like its poll-failure sibling ~40 lines down:
        // the pasted-URL site-parse flow (LEV-1431/1422) is not a tasks-v4 or
        // proto-blocks client feature — it runs for every founder who pastes a
        // URL and is gated server-side by the materials-parsing session stamp,
        // never by a flag in this component. This is a copy-parity fix on an
        // existing ungated path (one toast for both failure branches), not a new
        // flagged behavior; gating only this branch while the identical sibling
        // stays ungated would re-open the silent-failure gap for POST rejections.
        toast.error("We had trouble reading your website, but let's keep going.");
        return false;
      }
      if (program) trackProgramMaterialsSubmitted({ program, kind: "website" });
      // The route's fast path now covers the site (programContext.ts siteShared).
      if (landsOnCanvas) materialsFastPathRef.current.add("site");

      // Accepted (202) or already on file (200) — either way the site slot is
      // the server's; watch it settle IN THE BACKGROUND (the caller's chat turn
      // is waiting on this promise and must not wait on the poll).
      inFlightParsesCountRef.current += 1;
      setInFlightParses(c => c + 1);
      void (async () => {
        let pollOk = false;
        let readStillDue: boolean | undefined;
        try {
          const poll = await pollMaterialsParseStatus("site", signal);
          if (signal.aborted || !isCurrentEpoch()) return;
          pollOk = poll.ok;
          if (poll.ok) {
            readStillDue = poll.readDue === undefined ? true : poll.readDue.includes("site");
            trackMaterialsParsed({ sourceType: "site", outcome: "success" });
          } else {
            // The server lets a `failed` slot be re-staged, so the client dedupe
            // must forget the host too — otherwise pasting the same URL again
            // never reaches the route (PR review, LEV-1422).
            parsedSiteHostsRef.current.delete(host);
            captureClientError(
              new Error(`Onboarding site parse poll failed: ${poll.reason}`),
              {
                feature: "onboarding-chat",
                errorType: "site-parse",
                impact: "degraded",
                httpStatus: poll.status,
                contexts: { siteParse: { phase: poll.phase, httpStatus: poll.status } },
              },
            );
            trackMaterialsParsed({ sourceType: "site", outcome: "failure" });
            trackOnboardingMaterialsParseFailed({
              sourceType: "site",
              phase: poll.phase,
              httpStatus: poll.status,
            });
            toast.error("We had trouble reading your website, but let's keep going.");
          }
        } finally {
          settleMaterialsParse("site", pollOk, epoch, readStillDue);
        }
      })();
      return true;
    },
    [ensureSession, trackMaterialsParsed, settleMaterialsParse, program, landsOnCanvas, inFlightParsesCountRef, materialsEpochRef, materialsFastPathRef, parsedSiteHostsRef, setInFlightParses, siteParseAbortRef],
  );

  // Ensure a usable server session cookie BEFORE the anonymous build/skip
  // handoff, then run it. Without this, a fire-and-forget mint that failed
  // (429 rate limit / network / blocked cookie) leaves the finalize push and
  // the post-signup claim cookieless — the old "We lost your idea" path. The
  // sessionStorage backup is the safety net if the mint still can't succeed,
  // but ensuring the cookie keeps the richer, server-authoritative path (incl.
  // the paid founder-identity scan) working. We proceed regardless of the
  // outcome — the backup guarantees the idea survives either way.
  const proceedToHandoff = useCallback(
    (finalResult: OnboardingResult) => {
      // Write the conversion backup with the final result at the definitive
      // handoff moment — IdeaChatInput owns the backup lifecycle. The per-turn
      // effect also writes it, but this guarantees the most complete snapshot
      // is present before navigation, independent of effect timing.
      saveOnboardingSessionBackup({
        result: finalResult,
        messages: messagesRef.current,
        rubricTypeDirty: rubricTypeDirtyRef.current,
      });
      // Re-check mount at FIRE time, not just schedule time: unmount aborts the
      // in-flight mint (resolves false), but .finally still runs — without this
      // guard a founder who navigated away mid-mint would still get onComplete
      // + the sign-up redirect. Mirrors the first-submit/deck deferred paths.
      void ensureSession().finally(() => {
        if (!isMountedRef.current) return;
        beginHandoff(finalResult);
      });
    },
    [ensureSession, beginHandoff, messagesRef, rubricTypeDirtyRef],
  );

  const handleSubmit = useCallback((overrideText?: string, opts?: SubmitOptions) => {
    const text = (overrideText ?? inputValue).trim();
    if (!text) return;

    // A programmatic notice never starts a turn during handoff, busy or idle.
    if (opts?.queueIfBusy && isHandoffActive()) return;

    // A queueIfBusy notice also waits behind a queued answer or a turn end the
    // dispatcher has not handled yet; sending it there would race that send.
    // Busy includes the first-turn mint / site-parse gate: its deferred
    // submit would otherwise start a second stream right after this one.
    if (
      opts?.queueIfBusy &&
      (isTurnBusy ||
        queuedAnswerRef.current !== null ||
        finishedTurnSeqRef.current !== handledTurnSeqRef.current)
    ) {
      // A second notice in one turn is dropped on purpose: the server already
      // has every staged deck, so one notice covers them.
      if (queuedNoticeRef.current === null) queuedNoticeRef.current = text;
      return;
    }

    // A turn is atomic: never start a second generation while one is in
    // flight (useObject.submit does not abort the running stream; two
    // streams write one object). The founder's own answer in the result tail
    // queues for the turn-end dispatcher, as does a queueIfBusy notice
    // (above); anything else while busy (the nudge, a second answer) is
    // dropped. Both return before any side effect, so a held send changes
    // nothing yet (e.g. the materials-gate suppression).
    if (isTurnBusy) {
      if (
        overrideText === undefined &&
        canQueueAnswer &&
        queuedAnswerRef.current === null
      ) {
        setQueuedAnswer(text);
        setInputValue("");
      }
      return;
    }

    clearIntroDelay();

    if (introState === "waiting" || introState === "streaming") {
      showFullIntro();
    }

    // First message needs minimum length — but allow short messages containing a URL
    if (!hasStartedRef.current && text.length < MIN_IDEA_LENGTH && !extractFirstUrl(text)) {
      toast.error("Please provide a bit more detail about your idea.");
      return;
    }

    // The founder is carrying on from here, so the chat on screen is the one
    // to keep: a later remount must not replace it with the saved snapshot.
    clearResumeChat();

    // Reset declined build so new "complete" signals are honored
    setUserDeclinedBuild(false);
    // A new message clears parse-gate suppression: the new turn will reach
    // its own "complete" determination independently. It also replaces a
    // held nudge — the founder's turn carries the critique.
    if (suppressedCompleteRef.current) {
      trackOnboardingBuildCtaGated({ outcome: "released", wasSuppressed: gateWasSuppressedRef.current });
    }
    suppressedCompleteRef.current = false;
    setIsMaterialsSuppressed(false);
    nudgeHeldRef.current = false;
    gateWasSuppressedRef.current = false;
    readLostRef.current = new Set();

    const currentUserMessageCount = messagesRef.current.filter((m) => m.role === "user").length;

    // Track first message as chat started
    const isFirstMessage = !hasStartedRef.current;
    // On the first message, hold the session-mint promise so the first /chat
    // submit can await it (see submitChat below). The mint sets the HttpOnly
    // cookie the chat route reads for the session's materials context; without
    // waiting, the first turn arrives cookieless → the server has no materials
    // to inject. The mint is time-bounded (SESSION_MINT_TIMEOUT_MS), so this
    // cannot hang.
    let firstTurnMintPromise: Promise<boolean> | null = null;
    if (isFirstMessage) {
      trackOnboardingChatStarted({ messageLength: text.length });
      firstTurnMintPromise = mintSessionOnce();
      // Clear old conversation when user starts a new conversation
      if (showRestorePrompt) {
        consumeOnboardingChat();
        setShowRestorePrompt(false);
        setSavedChat(null);
      }
    }

    // Submit to the chat route, but hold the request while a pre-submit
    // dependency settles — `gate` is one of:
    // - the session mint, on the first turn, so the request carries the cookie
    //   (correct prompt verdict from message one, ON and OFF);
    // - the `{ url }` materials POST, on a turn that stages a website — its
    //   promise already awaited the mint, so it subsumes the first case. The
    //   chat route reads the site's processing marker from `materials` when it
    //   builds the prompt; a POST still in flight when the turn arrives means
    //   the model answers without [MATERIALS_PARSING] and without its hard
    //   gate (LEV-1422 review).
    // The user's message is already rendered optimistically (commitMessages
    // below), so only the AI reply waits — and only for bounded work (the mint
    // is time-bounded; the POST is one marker write + event send, and a failed
    // POST releases the gate too, so the message is never lost). While gated,
    // the composer is disabled (isAwaitingFirstMint) so a second send can't
    // jump ahead and reorder the payload. Turns with no gate submit
    // immediately.
    const submitChat = (payload: Parameters<typeof submit>[0], gate: Promise<unknown> | null) => {
      if (!gate) {
        startGeneration(submit);
        submit(payload);
        return;
      }
      setIsAwaitingFirstMint(true);
      // A per-send capture, not a flag: the next conversation's first send
      // must not re-arm a held send from the discarded one (it would carry the
      // old messages into the new session).
      const generation = conversationGenerationRef.current;
      const runGatedSubmit = () => {
        // Stale: return before touching state — the flag now belongs to the
        // next conversation's own held send.
        if (!isMountedRef.current || conversationGenerationRef.current !== generation) return;
        setIsAwaitingFirstMint(false);
        startGeneration(submit);
        submit(payload);
      };
      void gate.then(runGatedSubmit, runGatedSubmit);
    };

    // Track every message sent
    trackOnboardingChatMessageSent({
      messageNumber: currentUserMessageCount + 1,
      messageLength: text.length,
    });
    trackLandingMessageEvent(
      currentUserMessageCount + 1,
      text.length,
      opts?.source ?? (overrideText !== undefined ? "file_upload" : "text_input"),
    );

    hasStartedRef.current = true;
    setIsExpanded(true);
    // Reset timeout state for new submission
    setHasTimedOut(false);
    setTurnFailed(false);
    turnMessageCommittedRef.current = false;
    completionAcceptedRef.current = false;

    // Read from messagesRef (synced synchronously by handleObjectFinish) instead
    // of the closure `messages`, which can be stale if the user submits before
    // React re-renders after the previous assistant message was committed.
    const currentMessages = messagesRef.current;
    const newMessages = appendUserAnswer(currentMessages, text);
    commitMessages(newMessages);
    setInputValue("");

    // A local reply is a whole turn with no request. The server learns of it
    // with the next turn, whose body carries the full history: persistTurn
    // stores that history and the model reads the reply as already said.
    if (opts?.localReply && isFirstMessage) {
      commitMessages([...newMessages, { role: "assistant", content: opts.localReply }]);
      markTurnFinished();
      return;
    }

    // A LinkedIn sent while a shortened program chat's read still runs needs
    // no model turn: Lev's answer is fixed, and the model would only stall
    // for it. The route records the link on the next turn from the history
    // (promptContext.ts detectLatestLinkedInAnswer), and the parse landing
    // brings that turn (decideMaterialsNudge: the line asks nothing).
    if (
      landsOnCanvas &&
      materialsFastPathRef.current.size > 0 &&
      inFlightParsesCountRef.current > 0 &&
      isBareLinkedInAnswer(newMessages)
    ) {
      commitMessages([
        ...newMessages,
        { role: "assistant", content: programReadingAck(materialsFastPathRef.current) },
      ]);
      markTurnFinished();
      return;
    }

    // A pasted website URL is staged for the server parse job like an uploaded
    // deck (LEV-1431) — once per host, and the founder's message goes out
    // UNCHANGED (the server injects the site context into the system prompt
    // itself). The chat turn waits for the staging POST to settle (see
    // submitChat) so the processing marker is on file before the chat route
    // reads `materials`; the status poll stays in the background. A
    // linkedin.com URL is the founder's profile, not their website: the chat
    // route's LinkedIn detection owns that one. Known limitation: v1 treats
    // the first non-LinkedIn URL as the company site (a personal portfolio or
    // a competitor link would be crawled and gap-filled as company facts);
    // source-intent classification is a recorded follow-up to the LEV-1422 PR.
    let siteParseGate: Promise<boolean> | null = null;
    let siteOnly = false;
    const siteUrl = extractFirstUrl(text);
    if (siteUrl) {
      const host = new URL(siteUrl).hostname.toLowerCase();
      if (!isLinkedInHost(host) && !parsedSiteHostsRef.current.has(host)) {
        parsedSiteHostsRef.current.add(host);
        siteParseGate = requestSiteParse(siteUrl, host);
        siteOnly = textBesidesUrl(text, host).length <= PROGRAM_SITE_ONLY_MAX_EXTRA_CHARS;
      }
    }

    // `program` rides only when set, so the homepage body is unchanged.
    const payload = program ? { messages: newMessages, program } : { messages: newMessages };

    // A pasted website opening a shortened program chat gets the fixed
    // LinkedIn ask, like a dropped deck, once its read is queued. If it was
    // not, the model answers instead: the fixed reply claims a read.
    if (siteParseGate && siteOnly && isFirstMessage && landsOnCanvas) {
      setIsAwaitingFirstMint(true);
      const generation = conversationGenerationRef.current;
      const answerSiteGate = (queued: boolean) => {
        if (!isMountedRef.current || conversationGenerationRef.current !== generation) return;
        setIsAwaitingFirstMint(false);
        if (!queued) {
          startGeneration(submit);
          submit(payload);
          return;
        }
        commitMessages([
          ...messagesRef.current,
          { role: "assistant", content: PROGRAM_SITE_FIRST_REPLY },
        ]);
        markTurnFinished();
      };
      // A rejected gate MUST still release the composer, as submitChat's
      // gate.then(run, run) does: it counts as a read that was not queued.
      void siteParseGate.then(answerSiteGate, (error: unknown) => {
        captureClientError(error, {
          feature: "onboarding-chat",
          errorType: "site-parse",
          impact: "degraded",
          contexts: { siteParse: { phase: "stage" } },
        });
        answerSiteGate(false);
      });
      return;
    }

    submitChat(payload, siteParseGate ?? firstTurnMintPromise ?? deckStagingRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- messages accessed via messagesRef to avoid stale-closure race
  }, [inputValue, submit, showRestorePrompt, canQueueAnswer, isTurnBusy, introState, clearIntroDelay, showFullIntro, trackLandingMessageEvent, requestSiteParse, mintSessionOnce, setQueuedAnswer, isHandoffActive, startGeneration, program, markTurnFinished, landsOnCanvas]);
  handleSubmitRef.current = handleSubmit;

  // Resends the failed turn's history as-is: the founder's message is already
  // the last entry in `messages`, so routing through handleSubmit would
  // append it a second time.
  const canRetryTurn =
    turnFailed && !isTurnBusy && messages[messages.length - 1]?.role === "user";
  const retryFailedTurn = useCallback(() => {
    const current = messagesRef.current;
    if (isTurnBusy || current[current.length - 1]?.role !== "user") return;
    trackOnboardingChatRetryClicked({
      messageCount: current.filter((m) => m.role === "user").length,
    });
    setTurnFailed(false);
    setHasTimedOut(false);
    turnMessageCommittedRef.current = false;
    completionAcceptedRef.current = false;
    startGeneration(submit);
    submit(program ? { messages: current, program } : { messages: current });
  }, [isTurnBusy, program, startGeneration, submit, messagesRef]);

  // Turn-end dispatcher: at most ONE send per finished turn, in priority
  // order: queued founder answer, then deck notice, then a held nudge. Each
  // later item waits for the turn the earlier one starts to end.
  // Gated on finishedTurnSeq (set in onFinish/timeout/error) AND idle, so Lev's
  // reply is already committed and the answer lands after it.
  useEffect(() => {
    if (streamPhase !== 'idle' || finishedTurnSeq === handledTurnSeqRef.current) return;
    handledTurnSeqRef.current = finishedTurnSeq;
    if (lastTurnFailedRef.current) return;
    // A queued answer or notice spends a held nudge: the parse landed before
    // this send, so the server's [MATERIALS_READ_DUE] already asks Lev for the
    // read in the turn it starts. handleSubmit clears the held nudge and sends
    // the gate's one "released" event; releasing here too would double-count.
    const queued = queuedAnswerRef.current;
    if (queued !== null) {
      setQueuedAnswer(null);
      handleSubmitRef.current(queued, { source: "text_input" });
      return;
    }
    const notice = queuedNoticeRef.current;
    queuedNoticeRef.current = null;
    if (notice !== null && !isHandoffActive()) {
      handleSubmitRef.current(notice);
      return;
    }
    tellReadUnreadable();
    if (!nudgeHeldRef.current) return;
    nudgeHeldRef.current = false;
    decideMaterialsNudge();
  }, [streamPhase, finishedTurnSeq, setQueuedAnswer, decideMaterialsNudge, isHandoffActive, tellReadUnreadable, nudgeHeldRef]);

  // The Build CTA ends the chat's turn-taking: a queued answer goes back into
  // the composer (visible again on "keep chatting"); a deck notice and a held
  // nudge are moot (the server already has the deck).
  useEffect(() => {
    if (!showBuildConfirm) return;
    handBackQueuedAnswer();
    queuedNoticeRef.current = null;
    dropHeldNudge();
  }, [showBuildConfirm, handBackQueuedAnswer, dropHeldNudge]);

  const handleSkip = useCallback(() => {
    // Skip hands off without another turn, so a queued answer, deck notice
    // and held nudge are dropped and the gate the nudge was holding is released.
    setQueuedAnswer(null);
    queuedNoticeRef.current = null;
    dropHeldNudge();
    // If streaming, defer skip until stream completes
    if (isLoading && !hasTimedOut) {
      setPendingSkip(true);
      return;
    }

    // Execute skip immediately
    executeSkipAction();
  }, [isLoading, hasTimedOut, executeSkipAction, setQueuedAnswer, dropHeldNudge]);

  // Clears every piece of client state tied to the discarded conversation.
  // Callers MUST have detached the server session first (handleReset).
  const resetConversation = useCallback(() => {
    conversationGenerationRef.current += 1;
    // Unconditional: the isLoading captured when Reset was clicked is stale
    // after handleReset's awaits, and stop() is a no-op when idle.
    stop();
    // Also clears useObject's `error`, which setTurnFailed(false) below
    // cannot reach: without this a dead-provider error from the discarded
    // conversation keeps rendering the failed-turn notice after reset.
    clear?.();
    clearIntroDelay();
    clearIntroStream();
    // Clear pending actions. Marking every finished turn handled keeps the
    // dispatcher from acting on a turn that ended before this reset.
    setPendingSkip(false);
    setWaitingForStream(false);
    setQueuedAnswer(null);
    setTurnFailed(false);
    queuedNoticeRef.current = null;
    handledTurnSeqRef.current = finishedTurnSeqRef.current;
    completionAcceptedRef.current = false;
    setIsAwaitingFirstMint(false);
    resetMaterialsParse();
    // Clear messages and sessionStorage
    resetConversationState();
    setInputValue("");
    setShowBuildConfirm(false);
    setUserDeclinedBuild(false);
    setIsBuildingProject(false);
    setIsExpanded(false);
    setIntroState("done");
    setIntroStreamedContent(LEV_GREETING_DETAILS);
    programFirstReadTrackedRef.current = false;
    hasStartedRef.current = false;
    hasAutoProceedTriggeredRef.current = false;
    // cancel() before consumeOnboardingChat() so the streaming→idle flush, the
    // 500 ms timer, and the unmount flush find nothing to write. Without this,
    // the pending closure holds the pre-reset messages and re-saves them.
    cancelSessionPersist();
    consumeOnboardingChat();
    // consumeOnboardingChat() copies the chat into SS_ONBOARDING_CHAT_BACKUP, and
    // the conversion backup (SS_ONBOARDING_SESSION_BACKUP) survives too. Without a
    // destructive clear AFTER consume, restoreOnboardingFromBackup() resurrects the
    // discarded chat on reload and the claim path reuses it as fallback data. The
    // stashed claim token goes too: it names the detached session.
    clearOnboardingBackups();
    clearResumeChat();
    setShowResetConfirm(false);
    // Clear restore prompt so it doesn't re-appear after reset
    setShowRestorePrompt(false);
    setSavedChat(null);
  }, [stop, clear, clearIntroDelay, clearIntroStream, cancelSessionPersist, setQueuedAnswer, resetConversationState, resetMaterialsParse]);

  // "Start over": detach the server session BEFORE clearing the UI. On failure
  // the browser may still carry the old cookie, and a fresh-looking chat would
  // keep merging into the discarded session — so the conversation stays and
  // the founder can retry.
  const handleReset = useCallback(async () => {
    if (isResettingRef.current) return;
    isResettingRef.current = true;
    setIsResetting(true);
    // A saved chat behind the Resume prompt is not in messagesRef yet, but
    // it is what this reset discards.
    const saved = hasStartedRef.current ? null : savedChatRef.current;
    const discarded = saved
      ? {
          messageCount: saved.messages.filter((m) => m.role === "user").length,
          hadResult: saved.result != null,
        }
      : {
          messageCount: messagesRef.current.filter((m) => m.role === "user").length,
          hadResult: accumulatedResultRef.current !== null,
        };
    try {
      // Waits out any in-flight mint first: one that lands after the DELETE
      // would bind the browser to a session nobody detached.
      const detached = await detachSession();
      if (!isMountedRef.current) return;
      trackOnboardingChatReset({ ...discarded, detachOk: detached });
      if (!detached) {
        captureClientError(new Error("Onboarding reset: session detach failed"), {
          feature: "onboarding-chat",
          errorType: "session-detach",
          impact: "degraded",
        });
        toast.error("We couldn't start over just now. Please try again.");
        return;
      }
      const starter = pendingStarterRef.current;
      pendingStarterRef.current = null;
      resetConversation();
      if (starter) setStarterAfterReset(starter);
    } finally {
      isResettingRef.current = false;
      if (isMountedRef.current) setIsResetting(false);
    }
  }, [resetConversation, detachSession, accumulatedResultRef, messagesRef]);

  // Auto-call onComplete when isReady is set (for Skip flow - confirmation flow calls it directly)
  useEffect(() => {
    if (!isReady || !result) return;
    // Skip if already triggered (e.g., from handleConfirmBuild)
    if (hasAutoProceedTriggeredRef.current) return;
    hasAutoProceedTriggeredRef.current = true;
    // eslint-disable-next-line no-console -- intentional onboarding diagnostic log
    console.log("[IdeaChatInput] Auto-proceeding after Skip");
    setIsBuildingProject(true);
    proceedToHandoff(result);
  }, [isReady, result, proceedToHandoff]);

  // Handle user confirming they want to build
  const handleConfirmBuild = useCallback(() => {
    // Prevent multiple calls (can happen with effect re-runs or double-clicks)
    if (hasAutoProceedTriggeredRef.current) {
      console.log("[IdeaChatInput] Ignoring duplicate handleConfirmBuild call");
      return;
    }

    // If stream is still in progress, wait for it to finish
    if (isLoading) {
      setWaitingForStream(true);
      return;
    }

    // Stream is done — use cumulative accumulator (never resets between turns).
    // Falls back to the completion-captured `result` state.
    const finalResult = accumulatedResultRef.current ?? result;
    if (!finalResult) return;
    console.log("[IdeaChatInput] Final onboarding result at build confirm", {
      status: finalResult.status,
      journeyStage: finalResult.journey?.stage ?? null,
      oneLiner: finalResult.bigIdea?.oneLiner ?? null,
      refinedIdeaLength: finalResult.bigIdea?.refinedIdea?.length ?? 0,
      refinedIdeaPreview: finalResult.bigIdea?.refinedIdea?.slice(0, 120) ?? null,
    });
    hasAutoProceedTriggeredRef.current = true;
    // eslint-disable-next-line no-console -- intentional onboarding diagnostic log
    console.log("[IdeaChatInput] User confirmed build, proceeding to canvas");

    // Track build confirmation
    trackOnboardingChatBuildConfirmed({
      messageCount: messages.filter((m) => m.role === "user").length,
      hasIdentity: !!(finalResult.identity?.name || finalResult.identity?.linkedinUrl),
      hasJourney: finalResult.journey?.stage !== "unknown",
      hasSiteParse: parsedSiteHostsRef.current.size > 0,
    });
    trackLandingConversionEvent("build_confirmed", finalResult);

    setShowBuildConfirm(false);
    setIsBuildingProject(true);
    setIsReady(true);
    setResult(finalResult);
    // Don't clear chat here - it gets cleared after successful project creation
    // This allows user to click Back from sign-up and restore conversation
    proceedToHandoff(finalResult);
  }, [result, proceedToHandoff, messages, isLoading, trackLandingConversionEvent, accumulatedResultRef, setResult, parsedSiteHostsRef]);

  // When user clicked Build while streaming, proceed once stream finishes.
  // Uses the ref accumulator (always current, never one render behind) so
  // fields streamed in the tail (bigIdeaContent, identity) are not lost.
  useEffect(() => {
    if (!waitingForStream || isLoading) return;
    const finalResult = accumulatedResultRef.current ?? stableResult ?? result;
    if (!finalResult) return;
    console.log("[IdeaChatInput] Final onboarding result after waiting for stream", {
      status: finalResult.status,
      journeyStage: finalResult.journey?.stage ?? null,
      oneLiner: finalResult.bigIdea?.oneLiner ?? null,
      refinedIdeaLength: finalResult.bigIdea?.refinedIdea?.length ?? 0,
      refinedIdeaPreview: finalResult.bigIdea?.refinedIdea?.slice(0, 120) ?? null,
    });
    setWaitingForStream(false);
    setResult(finalResult);
    if (hasAutoProceedTriggeredRef.current) return;
    hasAutoProceedTriggeredRef.current = true;

    // eslint-disable-next-line no-console -- intentional onboarding diagnostic log
    console.log("[IdeaChatInput] Stream finished after Build click, proceeding to canvas");

    trackOnboardingChatBuildConfirmed({
      messageCount: messages.filter((m) => m.role === "user").length,
      hasIdentity: !!(finalResult.identity?.name || finalResult.identity?.linkedinUrl),
      hasJourney: finalResult.journey?.stage !== "unknown",
      hasSiteParse: parsedSiteHostsRef.current.size > 0,
    });
    trackLandingConversionEvent("build_confirmed", finalResult);

    setShowBuildConfirm(false);
    setIsBuildingProject(true);
    setIsReady(true);
    proceedToHandoff(finalResult);
  }, [
    waitingForStream,
    isLoading,
    stableResult,
    result,
    messages,
    proceedToHandoff,
    trackLandingConversionEvent,
    accumulatedResultRef,
    setResult,
    parsedSiteHostsRef,
  ]);

  // Handle user declining to build (continue chatting)
  const handleDeclineBuild = useCallback(() => {
    // eslint-disable-next-line no-console -- intentional onboarding diagnostic log
    console.log("[IdeaChatInput] User declined build, continuing chat");

    // Track build declined
    trackOnboardingChatBuildDeclined({
      messageCount: messages.filter((m) => m.role === "user").length,
    });

    setShowBuildConfirm(false);
    setUserDeclinedBuild(true);
  }, [messages]);

  // Minimize chat (collapse to inline mode)
  const handleMinimize = useCallback(() => {
    setIsExpanded(false);
  }, []);

  // Expand chat when user interacts with it
  const handleExpand = useCallback(() => {
    if (hasStartedRef.current && !isExpanded) {
      setIsExpanded(true);
    }
  }, [isExpanded]);

  // File upload handlers (PDF, DOCX, PPTX, TXT, MD)
  //
  // PDF and PPTX use the asynchronous materials pipeline: PDF gets page-image
  // vision; PPTX gets exact OOXML text extraction and text-only synthesis. A
  // client-side text extraction runs in parallel and rides along as
  // clientTextPreview so the model has a fast read while vision completes.
  const handleFile = useCallback(async (file: File, source: "drag_drop" | "picker") => {
    const onboardingDeckMaxBytes = UPLOAD_NAMESPACES["onboarding-decks"].maxBytes;
    const trackFileFailure = (kind: DeckUploadFailureKind, phase: DeckUploadPhase) =>
      trackDeckUploadFailed({
        kind,
        surface: "onboarding",
        format: deckUploadFormatOf(file.name),
        sizeBucket: deckUploadSizeBucket(file.size),
        phase,
      });
    // One toast + one deck_upload_failed event per failed file. Callers own
    // the isStale/epoch check: a discarded chat never gets either.
    const reportFileFailure = (
      kind: DeckUploadFailureKind,
      phase: DeckUploadPhase,
      size?: { limitBytes?: number; sizeBytes?: number },
    ) => {
      toast.error(
        deckUploadFailureMessage(kind, {
          ...size,
          accepted: ONBOARDING_ACCEPTED_FILES,
          format: deckUploadFormatOf(file.name),
          canPaste: true,
        }),
      );
      trackFileFailure(kind, phase);
    };
    const fileKind = getDocumentKind(file);
    if (!fileKind) {
      reportFileFailure("unsupported_format", "pre_upload");
      return;
    }
    const isDeckFile = fileKind === "pdf" || fileKind === "pptx";
    // PDF is rejected at the namespace cap here. PPTX passes through
    // DOCUMENT_UPLOAD_MAX_BYTES at this early check; the namespace cap is
    // enforced again just before the blob upload (see below).
    const maxBytes = fileKind === "pdf"
      ? onboardingDeckMaxBytes
      : DOCUMENT_UPLOAD_MAX_BYTES;
    if (file.size > maxBytes) {
      reportFileFailure("too_large", "pre_upload", { limitBytes: maxBytes, sizeBytes: file.size });
      return;
    }
    // The user added a valid file — track that action now, at the closest
    // reliable boundary. It is independent of whether the parse later succeeds
    // (analytics-conventions.md), so it must NOT be gated on the vision result.
    trackLandingFileEvent(fileKind, source);
    setIsParsingFile(true);
    const generation = conversationGenerationRef.current;
    const isStale = () =>
      !isMountedRef.current || conversationGenerationRef.current !== generation;
    // A shortened program chat's opening deck gets Lev's fixed reply now, not
    // after the upload: the reply needs nothing from it. From here the read
    // counts as in flight, so a LinkedIn sent mid-upload gets the fixed
    // "still reading" line. `staged` turns true once the parse POST is
    // accepted; the finally below retracts the reply if it never is.
    let announced = false;
    let staged = false;
    let releaseDeckStaging = () => {};
    const announceEpoch = materialsEpochRef.current;
    if (isDeckFile && landsOnCanvas && !hasStartedRef.current) {
      handleSubmitRef.current(composeDeckUploadNotice(file.name, { neutral: !!program }), {
        queueIfBusy: true,
        localReply: PROGRAM_DECK_FIRST_REPLY,
      });
      announced = hasStartedRef.current;
    }
    if (announced) {
      materialsFastPathRef.current.add("deck");
      inFlightParsesCountRef.current += 1;
      setInFlightParses(c => c + 1);
      const staging = new Promise<void>((resolve) => {
        releaseDeckStaging = () => {
          if (deckStagingRef.current === staging) deckStagingRef.current = null;
          resolve();
        };
      });
      deckStagingRef.current = staging;
      setIsUploadingAnnouncedDeck(true);
    }
    // Success dispatch for the non-deck (DOCX/TXT/MD) path. The deck path
    // calls handleSubmit directly because the conversation is already started.
    const finishUpload = (message: string) => {
      if (!hasStartedRef.current) {
        handleSubmitRef.current(message);
      } else {
        setInputValue(message);
      }
    };
    try {
      if (isDeckFile) {
        const serverStrategy = "server_vision";
        const parseDeckLocally = () =>
          fileKind === "pptx" ? parsePptxToText(file) : parsePdfToText(file);

        // Start text extraction immediately — no server dependency. Runs in
        // parallel with session mint so the clientTextPreview is ready by the
        // time we POST. Capture deferred until the server path is confirmed.
        let extractError: unknown;
        const textPromise = parseDeckLocally().catch((err) => {
          extractError = err;
          return null;
        });

        // Mint/confirm the session before the cookie-authenticated parse POST
        // (which 401s without it — LEV-1293).
        const sessionReady = await ensureSession();
        if (isStale()) return; // unmounted or reset during the mint

        // Server path: bail fast on a failed session mint BEFORE awaiting
        // text extraction (which can take 30s for large PDFs).
        if (!sessionReady) {
          captureClientError(
            new Error("Onboarding deck upload: session mint failed before parse"),
            {
              feature: "onboarding-chat",
              errorType:
                fileKind === "pdf"
                  ? "deck-vision-session"
                  : "deck-server-text-session",
              impact: "blocking",
              contexts: { deckParse: { fileSize: file.size } },
            },
          );
          trackMaterialsParsed({ sourceType: "deck", strategy: serverStrategy, outcome: "failure" });
          toast.error(
            "Lev couldn't start your upload just now. Try again in a moment.",
          );
          trackFileFailure("server_error", "upload");
          return;
        }

        // Session is usable — now await the text extraction for the POST body
        const extractedText = await textPromise;
        if (isStale()) return;

        // Capture any extraction error now that we know the server path is taken.
        // No-text is expected — the Inngest job does OOXML text extraction and
        // classifyDeckParseError captures an unreadable deck as degraded; the server
        // job owns that capture. Other errors are degraded but don't block the upload.
        // fixed on 2026-09-17: Sentry 7727687630
        if (extractError !== undefined) {
          captureClientError(extractError, {
            feature: "onboarding-chat",
            errorType: `${fileKind}-client-text-extract`,
            ...(isDeckNoTextError(extractError) ? { expected: true as const } : { impact: "degraded" as const }),
            contexts: { deckParse: { fileKind, fileSize: file.size } },
          });
        }

        // Server path (LEV-1293 + LEV-1295 + LEV-1368): upload directly to
        // private Blob, then POST a lightweight { deckBlob } reference.
        // Fresh abort controller for this parse; the unmount cleanup aborts it
        // so a late "ready" can't act after the founder navigates away.
        const abortController = new AbortController();
        visionParseAbortRef.current = abortController;
        const { signal } = abortController;
        const visionToastId = toast.loading(
          "Reading your deck page by page. This will only take a minute!",
        );
        try {
          if (signal.aborted) return;
          if (fileKind === "pptx" && file.size > onboardingDeckMaxBytes) {
            trackMaterialsParsed({ sourceType: "deck", strategy: serverStrategy, outcome: "failure" });
            setIsParsingFile(false);
            reportFileFailure("too_large", "pre_upload", {
              limitBytes: onboardingDeckMaxBytes,
              sizeBytes: file.size,
            });
            return;
          }

          // Step 1: Upload directly to Vercel Blob (bypasses the 4.5 MB
          // serverless body limit) via the shared deck-upload primitive. It
          // PUTs from the browser and sends the explicit contentType browsers
          // sometimes omit for .pptx — previously this path skipped that and
          // could be rejected by the namespace allowlist.
          let blobResult: { pathname: string };
          try {
            const { uploadDeck } = await import(
              "@/services/deckUpload/DeckUploadService"
            );
            blobResult = await uploadDeck({
              file,
              namespace: "onboarding-decks",
              format: fileKind === "pptx" ? "pptx" : "pdf",
              buildPathname: (format) =>
                `${ONBOARDING_DECK_CLIENT_PATH_PREFIX}${crypto.randomUUID()}.${format}`,
              handleUploadUrl: "/api/upload/onboarding-deck",
              abortSignal: signal,
            });
          } catch (uploadErr) {
            if (signal.aborted) return;
            // A tab left open across a deploy still references the old
            // build's hashed chunk filename, which the new deploy no longer
            // serves. "Try again" re-requests the same stale URL and fails
            // identically; only a refresh re-reads the current chunk
            // manifest. Capture the ORIGINAL error (never a fresh generic
            // one) so Sentry still shows the real cause.
            // Offline, the same chunk request fails too, but "try again"
            // is then the right advice, not a refresh.
            if (isChunkLoadError(uploadErr) && navigator.onLine !== false) {
              captureClientError(uploadErr, {
                feature: "onboarding-chat",
                errorType: "deck-upload-chunk-load",
                impact: "degraded",
                contexts: { deckParse: { phase: "blob-upload", fileSize: file.size } },
              });
              trackOnboardingMaterialsParseFailed({
                sourceType: "deck",
                phase: "blob-upload",
                httpStatus: null,
                fileSizeBucket: fileSizeBucket(file.size),
              });
              toast.error(
                "Lev was updated while this page was open. Refresh the page to upload your deck. Your chat is saved.",
              );
              // No deck_upload_failed: a stale tab is an app-version mismatch,
              // not a file failure, and would read as a network drop.
              return;
            }
            captureClientError(uploadErr, {
              feature: "onboarding-chat",
              errorType: "deck-blob-upload",
              impact: "blocking",
              contexts: { deckParse: { phase: "blob-upload", fileSize: file.size } },
            });
            trackOnboardingMaterialsParseFailed({
              sourceType: "deck",
              phase: "blob-upload",
              httpStatus: null,
              fileSizeBucket: fileSizeBucket(file.size),
            });
            reportFileFailure(classifyDeckUploadError(uploadErr), "upload", {
              limitBytes: onboardingDeckMaxBytes,
            });
            return;
          }
          if (signal.aborted || isStale()) return;

          // Step 2: POST the blob reference to the parse route. pathname
          // MUST be the PutBlobResult.pathname (addRandomSuffix: true means
          // the final pathname differs from what the client asked for).
          const postResult = await postMaterialsParse(
            {
              deckBlob: {
                pathname: blobResult.pathname,
                fileName: file.name,
                size: file.size,
              },
              ...(extractedText && { clientTextPreview: extractedText }),
            },
            signal,
          );
          if (signal.aborted || isStale()) return;

          if (!postResult.ok) {
            captureClientError(
              new Error(`Onboarding deck parser POST failed: ${postResult.reason}`),
              {
                feature: "onboarding-chat",
                errorType:
                  fileKind === "pdf"
                    ? "deck-vision-parse"
                    : "deck-server-text-parse",
                impact: "blocking",
                httpStatus: postResult.status,
                contexts: {
                  deckParse: {
                    phase: postResult.phase,
                    httpStatus: postResult.status,
                    fileSize: file.size,
                  },
                },
              },
            );
            trackMaterialsParsed({ sourceType: "deck", strategy: serverStrategy, outcome: "failure" });
            trackOnboardingMaterialsParseFailed({
              sourceType: "deck",
              phase: postResult.phase,
              httpStatus: postResult.status,
              fileSizeBucket: fileSizeBucket(file.size),
            });
            // Only the limit, never the file size: a server-side too_large on a
            // file the client measured under the cap would read as a contradiction.
            reportFileFailure(postResult.failureKind ?? "unknown", "upload", {
              limitBytes: onboardingDeckMaxBytes,
            });
            return;
          }

          // POST succeeded (202) — the Inngest job is queued. The text preview
          // is stored in the session marker and injected as [DECK_TEXT] by the
          // chat route. The visible message is short; the model's system prompt
          // has the full text. handleSubmit (not finishUpload) because the
          // conversation is already started and finishUpload would only fill
          // the textarea without sending.
          toast.dismiss(visionToastId);
          staged = true;
          if (program) trackProgramMaterialsSubmitted({ program, kind: "deck" });
          if (!announced) {
            // A deck that opens a shortened program chat needs no model turn to
            // ask for LinkedIn: the read is still running, so the reply is fixed.
            if (landsOnCanvas) materialsFastPathRef.current.add("deck");
            handleSubmitRef.current(composeDeckUploadNotice(file.name, { neutral: !!program }), {
              queueIfBusy: true,
              ...(landsOnCanvas && { localReply: PROGRAM_DECK_FIRST_REPLY }),
            });
          }

          // Poll for completion in the background — analytics + error toasts
          // only; the chat already has the [MATERIALS_PARSING] → [MATERIALS_CRITIQUE]
          // injection path via promptContext.ts. Also tracks the in-flight parse
          // count so the build confirm effect can suppress the CTA until the
          // materials critique has been delivered.
          const epoch = materialsEpochRef.current;
          const isCurrentEpoch = () => isMountedRef.current && epoch === materialsEpochRef.current;
          // An announced deck was counted when Lev announced it.
          if (!announced) {
            inFlightParsesCountRef.current += 1;
            setInFlightParses(c => c + 1);
          }
          void (async () => {
            let pollOk = false;
            let readStillDue: boolean | undefined;
            try {
              const poll = await pollMaterialsParseStatus("deck", signal, (progress) => {
                if (isCurrentEpoch()) setDeckProgress(progress);
              });
              if (signal.aborted || !isCurrentEpoch()) return;
              pollOk = poll.ok;
              if (poll.ok) {
                readStillDue = poll.readDue === undefined ? true : poll.readDue.includes("deck");
                trackMaterialsParsed({ sourceType: "deck", strategy: serverStrategy, outcome: "success" });
              } else {
                captureClientError(
                  new Error(`Onboarding deck parser poll failed: ${poll.reason}`),
                  {
                    feature: "onboarding-chat",
                    errorType:
                      fileKind === "pdf"
                        ? "deck-vision-parse"
                        : "deck-server-text-parse",
                    impact: "degraded",
                    httpStatus: poll.status,
                    contexts: {
                      deckParse: {
                        phase: poll.phase,
                        httpStatus: poll.status,
                        fileSize: file.size,
                      },
                    },
                  },
                );
                trackMaterialsParsed({ sourceType: "deck", strategy: serverStrategy, outcome: "failure" });
                trackOnboardingMaterialsParseFailed({
                sourceType: "deck",
                  phase: poll.phase,
                  httpStatus: poll.status,
                  fileSizeBucket: fileSizeBucket(file.size),
                });
                reportFileFailure(poll.failureKind ?? "unknown", "read", {
                  limitBytes: onboardingDeckMaxBytes,
                });
              }
            } finally {
              if (isCurrentEpoch()) setDeckProgress(null);
              settleMaterialsParse("deck", pollOk, epoch, readStillDue);
            }
          })();
          return;
        } finally {
          toast.dismiss(visionToastId);
        }
      }

      let text: string;
      if (fileKind === "docx") {
        text = await parseDocxToText(file);
      } else {
        text = await parseTextLikeFile(file, fileKind);
      }
      if (isStale()) return;
      if (!text) {
        reportFileFailure("no_text", "read");
        return;
      }
      // A DOCX/TXT/MD never hits the materials POST; its text going into the
      // chat is the closest boundary for the "document" kind.
      if (program) trackProgramMaterialsSubmitted({ program, kind: "document" });
      // Only a program lander treats this as a possible application upload;
      // the homepage composer's DOCX/TXT/MD path is unchanged (LEV-2518).
      finishUpload(program ? trimProgramApplicationText(text) : text);
    } catch (err) {
      // Any document parse failure (DOCX/TXT/MD, or a deck server-path throw)
      // is USER-BLOCKING — the founder's upload just dead-ended in a toast.
      // Previously these were toast-only and invisible in Sentry.
      captureClientError(err, {
        feature: "onboarding-chat",
        errorType: "doc-parse",
        impact: "blocking",
        contexts: { docParse: { fileKind, fileSize: file.size } },
      });
      // A parse from a discarded chat still reports its fault, but never toasts
      // into the conversation that replaced it.
      // Never the raw message: parser internals ("invalid DOCX central
      // directory") tell a founder nothing they can act on.
      if (!isStale()) {
        reportFileFailure(classifyDeckReadError(err), "read");
      }
    } finally {
      // Don't setState after unmount (the parse can run up to 5 minutes — the
      // founder may have navigated away by the time it settles).
      if (isMountedRef.current) setIsParsingFile(false);
      if (announced) {
        // Every exit MUST release it, or a turn held on it never goes out.
        releaseDeckStaging();
        if (isMountedRef.current) setIsUploadingAnnouncedDeck(false);
        if (!staged && !isStale()) abandonAnnouncedDeck(announceEpoch);
      }
    }
  }, [trackLandingFileEvent, trackMaterialsParsed, ensureSession, settleMaterialsParse, abandonAnnouncedDeck, program, landsOnCanvas, deckStagingRef, inFlightParsesCountRef, materialsEpochRef, materialsFastPathRef, setDeckProgress, setInFlightParses, visionParseAbortRef]);

  const onFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleFile(file, "picker");
    e.target.value = ""; // Reset to allow re-uploading same file
  }, [handleFile]);

  const onPickFile = useCallback(() => fileInputRef.current?.click(), []);

  const handleProgramUpload = useCallback(() => {
    if (!program) return;
    trackProgramCtaClicked({ program, action: "upload_deck" });
    onPickFile();
  }, [program, onPickFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    // The idle paperclip and drop zone track a click-time upload_deck before
    // the picker opens; a drop has no equivalent click, so it tracks here
    // once instead, never both for the same upload, and only while idle,
    // the same window those clicks track in.
    if (program && isProgramIdle) trackProgramCtaClicked({ program, action: "upload_deck" });
    await handleFile(file, "drag_drop");
  }, [handleFile, program, isProgramIdle]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter submits (Shift+Enter for newline)
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        if (canSend) {
          handleSubmit();
        } else if (answerMustWait && inputValue.trim()) {
          setAnswerWaitHint(true);
          if (!hintFiredForTurnRef.current) {
            hintFiredForTurnRef.current = true;
            trackOnboardingAnswerBeforeReply();
          }
        }
      }
    },
    [canSend, handleSubmit, answerMustWait, inputValue],
  );

  // Render chat content (used in both inline and expanded modal modes)
  const renderChatContent = (expanded: boolean) => (
    <>
      {/* Progress bar - shows after conversation starts */}
      {hasStarted && deckProgress && <DeckParseProgressBar progress={deckProgress} />}
      {hasStarted && !deckProgress && (
        <div className="px-6 pt-4 pb-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-landing-secondary">Understanding your idea...</span>
            <span className="text-xs font-medium text-landing-dark">{progressPercent}%</span>
          </div>
          <div className="h-1.5 bg-landing-bg-medium/50 rounded-full overflow-hidden">
            <div
              className="h-full bg-lev-orange rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}


      {/* Messages area */}
      <div
        ref={scrollRef}
        onScroll={handleChatScroll}
        className={cn(
          "overflow-y-auto px-6 pt-5 pb-3 space-y-3",
          hideGreeting && !hasStarted && !error && "hidden",
        )}
        style={expanded ? { height: EXPANDED_CHAT_HEIGHT } : { maxHeight: INLINE_CHAT_MAX_HEIGHT }}
      >
        {committedMessages.map((m, i) => {
          // Don't render empty messages
          if (!m.content.trim()) return null;

          if (landsOnCanvas && m.role === "user" && m.content === MATERIALS_NUDGE_MESSAGE) {
            return (
              <p key={`msg-${i}`} className="px-3 py-1 text-center text-xs text-slate-500">
                {MATERIALS_NUDGE_STATUS_LINE}
              </p>
            );
          }

          return (
            <div
              key={`msg-${i}`}
              className={cn(
                "w-fit max-w-[80%] rounded-xl px-3 py-2 text-base leading-relaxed text-landing-dark",
                m.role === "user"
                  ? "ml-auto bg-slate-100 whitespace-pre-wrap break-words overflow-hidden"
                  : "mr-auto bg-lev-orange/10",
              )}
            >
              {m.role === "assistant" ? (
                <div className="break-words overflow-hidden">
                  <ChatMarkdown content={m.content} />
                </div>
              ) : (
                expandLetterChoice(
                  m.content,
                  committedMessages[i - 1]?.role === "assistant"
                    ? committedMessages[i - 1].content
                    : undefined,
                )
              )}
            </div>
          );
        })}

        {introState === "waiting" && (
          <div className="mr-auto px-3 py-1">
            <p className="text-xs text-slate-500 italic shimmer">Thinking...</p>
          </div>
        )}

        {/* Show streaming message */}
        {streamingMessage && (
          <div
            {...{ [STREAM_ANCHOR_ATTR]: "" }}
            className={cn(
              "mr-auto w-fit max-w-[80%] rounded-xl bg-lev-orange/10 px-3 py-2 text-base leading-relaxed text-landing-dark",
            )}
          >
            <div className="break-words overflow-hidden">
              <ChatMarkdown content={streamingMessage} />
            </div>
          </div>
        )}

        {/* Busy for the whole turn, including the result tail after the
            visible text ends (it can run 30s+) and the first-turn mint wait. */}
        {isTurnBusy && (
          <div className="mr-auto px-3 py-1">
            <p className="text-xs text-slate-500 italic shimmer">Thinking...</p>
          </div>
        )}

        {/* Below Lev's streaming reply, never above it: the answer is not in
            `messages` until the turn-end dispatcher sends it. */}
        {queuedAnswer !== null && (
          <div
            className={cn(
              "w-fit max-w-[80%] rounded-xl px-3 py-2 text-base leading-relaxed text-landing-dark",
              "ml-auto bg-slate-100 whitespace-pre-wrap break-words overflow-hidden",
            )}
          >
            {expandLetterChoice(
              queuedAnswer,
              streamingMessage ||
                (committedMessages[committedMessages.length - 1]?.role === "assistant"
                  ? committedMessages[committedMessages.length - 1].content
                  : undefined),
            )}
          </div>
        )}

        {(turnFailed || error) && (
          <div role="alert" className="mr-auto flex items-center gap-3 px-3 py-1 text-sm text-landing-dark">
            <span>Lev couldn&apos;t answer that one.</span>
            {canRetryTurn && (
              <Button
                onClick={retryFailedTurn}
                variant="secondary"
                className="border-landing-bg-medium text-landing-dark hover:bg-landing-bg-medium/50 rounded-full px-3 py-1 text-sm max-sm:min-h-11 [@media(pointer:coarse)]:min-h-11"
              >
                Try again
              </Button>
            )}
          </div>
        )}

        {/* Materials parse suppression note: visible while a "complete" is
            suppressed because a deck/site parse is still in flight. */}
        {isMaterialsSuppressed && (
          <div role="status" className="text-xs text-landing-secondary text-center py-1">
            Still reading your materials. Lev will share its take before you build.
          </div>
        )}

        {/* Message limit warning */}
        {userMessageCount >= WARNING_THRESHOLD && userMessageCount < MAX_USER_MESSAGES && (
          <div className="text-xs text-landing-secondary text-center py-1">
            {MAX_USER_MESSAGES - userMessageCount} messages remaining
          </div>
        )}

        {/* Build confirmation prompt */}
        {showBuildConfirm && (
          <div className="mx-2 mt-3 p-4 bg-lev-orange/5 border border-lev-orange/20 rounded-2xl">
            <p className="text-sm text-landing-dark font-medium mb-3">
              I have enough information. Go ahead and build?
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={handleConfirmBuild}
                disabled={waitingForStream}
                // 44 px tap target on a phone or touch screen; desktop sizing unchanged.
                className="flex-1 bg-lev-orange text-white hover:bg-[#E64A00] rounded-full py-2 text-sm font-medium max-sm:min-h-11 [@media(pointer:coarse)]:min-h-11"
              >
                {waitingForStream ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Building&hellip;</>
                ) : (
                  "Yes, let\u2019s build!"
                )}
              </Button>
              <Button
                onClick={handleDeclineBuild}
                variant="secondary"
                className="flex-1 border-landing-bg-medium text-landing-dark hover:bg-landing-bg-medium/50 rounded-full py-2 text-sm max-sm:min-h-11 [@media(pointer:coarse)]:min-h-11"
              >
                No, keep chatting
              </Button>
            </div>
          </div>
        )}

        {/* Building indicator - shown after user confirms build */}
        {isBuildingProject && handoffState !== "failed" && (
          <div className="mx-2 mt-3 flex items-center gap-3 px-4 py-3">
            <Loader2 className="h-5 w-5 animate-spin text-lev-orange flex-shrink-0" />
            <p className="text-sm text-landing-dark font-medium" role="status" aria-live="polite">
              {buildingMessage}
            </p>
          </div>
        )}

        {/* Handoff failure - the finalize push (or project creation) didn't land; offer retry */}
        {handoffState === "failed" && (
          <div className="mx-2 mt-3 flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
            <p className="text-sm text-landing-dark font-medium" role="alert">
              We couldn&apos;t save your conversation. Check your connection. Nothing is lost.
            </p>
            <Button
              type="button"
              onClick={retryHandoff}
              className="rounded-full bg-lev-orange px-5 py-2 text-sm text-white hover:bg-lev-orange/90 sm:flex-shrink-0"
            >
              Try again
            </Button>
          </div>
        )}
      </div>

      {/* Program lander, empty chat only. T.A. feedback (2026-10-30): back to
          the bigger drop box (pre-Mockup-1 visuals), with an "or" divider and
          a called-out label handing off to the composer below, which is the
          "just start chatting" path. */}
      {isProgramIdle && (
        <div className="space-y-4 px-6 pb-2 pt-6" onClick={(e) => e.stopPropagation()}>
          {/* No price line (LEV-2713): "Free" here was redundant. */}
          <p className="text-base font-medium text-landing-dark">Start with what you have</p>
          <button
            type="button"
            onClick={handleProgramUpload}
            // A real press, like the composer's own mint below (LEV-1917):
            // the mint overlaps the file picker instead of opening the
            // upload chain only after the click resolves.
            onPointerDown={() => void mintSessionOnce()}
            disabled={isParsingFile || isSubmitting}
            aria-busy={isParsingFile}
            className={cn(
              "flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-lev-orange/50 focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-60",
              isDragging
                ? "border-lev-orange bg-lev-orange/10"
                : "border-lev-orange/40 bg-lev-orange/5 hover:border-lev-orange hover:bg-lev-orange/10",
            )}
          >
            {isParsingFile ? (
              <Loader2 className="h-7 w-7 animate-spin text-lev-orange" aria-hidden="true" />
            ) : (
              <FileUp className="h-7 w-7 text-lev-orange" aria-hidden="true" />
            )}
            <span className="text-base font-medium text-landing-dark">
              {isParsingFile
                ? "Reading your file"
                : isDragging
                  ? "Drop to upload your file"
                  : PROGRAM_DROP_ZONE_TITLE}
            </span>
            {/* Hidden, not removed, so the zone keeps its height across states. */}
            <span
              className={cn(
                // #5F6878, not landing-secondary: 4.0:1 on the peach fill fails AA.
                "text-sm text-[#5F6878]",
                (isDragging || isParsingFile) && "invisible",
              )}
            >
              {PROGRAM_DROP_ZONE_SUBTEXT}
            </span>
          </button>
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-landing-bg-medium" />
            <span className="text-xs uppercase tracking-wide text-landing-secondary">or</span>
            <div className="h-px flex-1 bg-landing-bg-medium" />
          </div>
          <p className="text-[15px] font-semibold text-landing-dark">Just start chatting with Lev</p>
        </div>
      )}

      {/* Input area - hidden when showing build confirmation or building.
          On the idle program card it sits under the "Just start chatting"
          label in the home page's own composer pill. */}
      {!showBuildConfirm && !isBuildingProject && (
        <div
          className={cn(
            "px-4 py-4 sm:px-6",
            isProgramIdle
              ? cn("mx-6 mb-6 bg-white/70 backdrop-blur-[8.75px]", COMPOSER_SURFACE_CLASSES)
              : "border-t border-landing-bg-medium/50",
          )}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canSend) {
                handleSubmit();
              }
            }}
            className="flex items-center gap-2 max-sm:flex-wrap sm:gap-3"
          >
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept={DOCUMENT_ACCEPT_STRING}
              className="hidden"
              onChange={onFileChange}
            />

            {/* PDF upload button, the same as the home page's. On the idle
                program card it is a second way into the drop zone's upload,
                so it tracks upload_deck the same way (once per click). */}
            <button
              type="button"
              onPointerDown={isProgramIdle ? () => void mintSessionOnce() : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (isProgramIdle) handleProgramUpload();
                else onPickFile();
              }}
              disabled={isParsingFile || isSubmitting || pendingSkip}
              className={cn(
                "flex-shrink-0 p-2 text-slate-400 hover:text-slate-600 transition-colors",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
              title="Click to upload PDF, DOCX, PPTX, TXT, or MD"
              aria-label="Upload file"
              aria-busy={isParsingFile}
            >
              {isParsingFile ? (
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                <Paperclip className="h-5 w-5" aria-hidden="true" />
              )}
            </button>

            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => {
                const next = e.target.value;
                setInputValue(next);
                // Covers keyboard users who tab into the box and start typing
                // without a pointer press. mintSessionOnce is idempotent, so
                // firing here as well as onPointerDown below is safe. fixed on 2026-09-25: LEV-1917
                if (next) void mintSessionOnce();
              }}
              onKeyDown={handleKeyDown}
              onFocus={handleExpand}
              onPointerDown={() => {
                // Mint on a real pointer press or a keystroke (onChange
                // above), never on focus: the browser fires "focus" on
                // page-load autofocus and refires it when the window
                // regains focus (tab away, tab back), and either one would
                // spend the per-IP mint budget (20/hr,
                // src/app/api/onboarding/session/route.ts) on a throwaway
                // session. fixed on 2026-09-25: LEV-1917
                void mintSessionOnce();
              }}
              placeholder={
                hasStarted
                  ? "Type your response..."
                  : programConfig
                    ? PROGRAM_IDLE_PLACEHOLDER
                    : INPUT_PLACEHOLDER
              }
              disabled={
                isSubmitting ||
                (isParsingFile && !isUploadingAnnouncedDeck) ||
                pendingSkip ||
                queuedAnswer !== null
              }
              rows={1}
              className={cn(
                // min-w-0: a flex-1 textarea otherwise refuses to shrink below its
                // content width and the fixed controls squeeze it to ~30px on a phone.
                "min-w-0 flex-1 bg-transparent text-landing-dark text-base tracking-[0.16px] outline-none resize-none",
                // Program pages MUST keep the placeholder at 4.5:1 on the white
                // composer (slate-500 is 4.76:1; #AAB0BB is 2.18:1).
                programConfig ? "placeholder:text-slate-500" : "placeholder:text-[#AAB0BB]",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "min-h-[24px] max-h-[240px] overflow-y-auto",
              )}
              style={{ height: MIN_TEXTAREA_HEIGHT }}
            />
            {hasStarted && (
              // display:contents keeps Reset and Skip in the desktop row; below sm
              // they wrap to their own row so the textarea gets the input row.
              <div className="contents max-sm:order-last max-sm:flex max-sm:basis-full max-sm:justify-end max-sm:gap-2">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  disabled={!canReset}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm flex-shrink-0",
                    "max-sm:min-h-10",
                    "text-landing-secondary hover:text-landing-dark transition-colors",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                >
                  <SkipBack className="h-4 w-4" aria-hidden="true" />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={!canSkip}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm flex-shrink-0",
                    "max-sm:min-h-10",
                    "text-landing-secondary hover:text-landing-dark transition-colors",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                >
                  {pendingSkip ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <SkipForward className="h-4 w-4" aria-hidden="true" />
                  )}
                  Skip
                </button>
              </div>
            )}
            <Button
              type="submit"
              size="icon"
              disabled={!canSend || isSubmitting || !inputValue.trim()}
              aria-label="Send message"
              aria-busy={queuedAnswer !== null}
              className={cn(
                "h-10 w-10 flex-shrink-0 rounded-full bg-lev-orange text-white transition-colors",
                "hover:bg-lev-orange-dark focus:outline-none focus:ring-2 focus:ring-lev-orange/50 focus:ring-offset-2",
                "disabled:cursor-not-allowed",
                "disabled:opacity-50",
                "shadow-lev-send-button",
              )}
            >
              {queuedAnswer !== null ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </Button>
          </form>
          {queuedAnswer !== null && (
            <p role="status" className="mt-2 text-xs text-landing-secondary">
              Your answer sends when Lev finishes.
            </p>
          )}
          {answerWaitHint && answerMustWait && (
            <p role="status" className="mt-2 text-xs text-landing-secondary">
              You can answer once Lev starts replying.
            </p>
          )}
        </div>
      )}
    </>
  );

  // Unified chat interface (idle + chatting)
  return (
    <>
      {/* When expanded, render overlay + chatbox via portal to cover entire viewport */}
      {isExpanded &&
        createPortal(
          <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
            {/* Overlay backdrop */}
            <div
              className="absolute inset-0 bg-black/30 animate-in fade-in duration-200"
              onClick={handleMinimize}
              aria-hidden="true"
            />
            {/* Expanded chatbox */}
            <div
              className={cn(
                "relative w-full max-w-2xl bg-white/95 backdrop-blur-[8.75px] border border-landing-bg-medium rounded-[29px] overflow-hidden",
                "shadow-[0_8px_32px_rgba(0,0,0,0.15)]",
                "animate-in zoom-in-95 duration-200",
              )}
              onClick={() => {
                if (!window.getSelection()?.toString()) textareaRef.current?.focus();
              }}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Drag overlay */}
              {isDragging && (
                <div
                  className="absolute inset-0 flex items-center justify-center rounded-[29px] bg-white/90 backdrop-blur-sm z-10"
                  role="status"
                  aria-live="polite"
                  aria-label="Drop file to upload"
                >
                  <span className="text-lg font-medium text-gray-500">Drop here to upload</span>
                </div>
              )}
              {renderChatContent(true)}
            </div>
            <FactsInspectorGate>
              <OnboardingFactsInspector refreshSignal={factsRefreshSignal} />
            </FactsInspectorGate>
          </div>,
          document.body,
        )}

      {/* Inline chatbox (when not expanded) */}
      {!isExpanded && (
        <div
          ref={inlineCardRef}
          className={cn(
            "relative w-full overflow-hidden",
            COMPOSER_SURFACE_CLASSES,
            // Opaque on a program lander: the hero photo behind it muddied the drop zone.
            programConfig ? "bg-white" : "bg-white/70 backdrop-blur-[8.75px]",
            className,
          )}
          onClick={() => {
            if (!window.getSelection()?.toString()) textareaRef.current?.focus();
          }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Drag overlay. There is no separate drop zone (Mockup 1): the
              composer itself is the drop target, idle or not. */}
          {isDragging && (
            <div
              className="absolute inset-0 flex items-center justify-center rounded-[29px] bg-white/90 backdrop-blur-sm z-10"
              role="status"
              aria-live="polite"
              aria-label="Drop file to upload"
            >
              <span className="text-lg font-medium text-gray-500">Drop here to upload</span>
            </div>
          )}
          {renderChatContent(false)}
        </div>
      )}

      {/* Restore conversation prompt - shown below chat when not expanded */}
      {!isExpanded && showRestorePrompt && savedChat && (
        // The negative margin tucks the link under the home pill; the taller
        // program card would cover it there (LEV-2858).
        <div className={cn("w-full text-left pl-6", programConfig ? "mt-3" : "-mt-[30px]")}>
          <button
            onClick={() => handleRestore("prompt")}
            className="text-sm text-lev-orange hover:underline"
          >
            Resume your previous conversation?
          </button>
        </div>
      )}

      {/* Placeholder to maintain layout when expanded */}
      {isExpanded && (
        <div
          className={cn(
            "w-full bg-white/30 backdrop-blur-[4px] border border-landing-bg-medium/50 rounded-[29px] overflow-hidden",
            "shadow-[0_4px_16px_rgba(0,0,0,0.04)]",
            programConfig && "flex items-center justify-center",
            className,
          )}
          style={programConfig && programCardHeight ? { minHeight: programCardHeight } : undefined}
        >
          <div className="px-6 py-8 text-center text-landing-secondary text-sm">
            Chat expanded above...
          </div>
        </div>
      )}

      {/* Reset confirmation modal - rendered via portal to appear above expanded chat */}
      {showResetConfirm &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reset-confirm-title"
            className="fixed inset-0 z-modal-confirmation flex items-center justify-center bg-black/70 px-6 py-12"
            aria-busy={isResetting}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !isResetting) closeResetConfirm();
            }}
          >
            <div className="w-full max-w-md rounded-2xl bg-white px-8 py-6 shadow-2xl">
              <div className="space-y-4">
                <header>
                  <h2
                    id="reset-confirm-title"
                    className="text-xl font-semibold text-slate-900"
                  >
                    Reset conversation?
                  </h2>
                  <p className="mt-2 text-sm text-slate-600">
                    This will clear your current conversation and start fresh. This action cannot be undone.
                  </p>
                </header>
              </div>
              <div className="mt-8 flex items-center justify-end gap-3">
                <Button
                  variant="secondary"
                  className="border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900"
                  onClick={closeResetConfirm}
                  disabled={isResetting}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red-600 text-white hover:bg-red-700"
                  onClick={() => void handleReset()}
                  disabled={isResetting}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Off while building: the Build/Skip handoff navigates only after the
          finalize fetch settles, so this has already stopped blocking by then. */}
      <NavigationBlocker
        isBlocking={inputValue.trim() !== "" && !isBuildingProject}
        message="You have a message you haven't sent. If you leave now, it will be lost."
      />
    </>
  );
}
